import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion'; 
import RadialMenu, { RadialMenuOption } from '../RadialMenu/RadialMenu'; 
import PokerChip from '../PokerChip/PokerChip';
import './PokerVisual.css';
import { ActionType, PlaygroundPosition, Street } from '../../pages/PlaygroundPage/PlaygroundPage';

// Define a type for the suit symbols object
type SuitSymbols = { [key: string]: string };
const SUIT_SYMBOLS: SuitSymbols = {c:'♣', h:'♥', d:'♦', s:'♠'};

interface SeatMeta { id: number; label: PlaygroundPosition; tag?: string; }

// --- Helper Functions (Ensure defined BEFORE use) ---

// Determine if a seat is SB or BB
const getSeatTag = (seatId: number, seats: SeatMeta[], smallBlind: number, bigBlind: number): string | undefined => {
  const seat = seats.find(s => s.id === seatId);
  if (!seat) return undefined;
  if (seat.label === 'SB') return `SB`; 
  if (seat.label === 'BB') return `BB`;
  return undefined;
};

interface PokerVisualProps {
  seatCount: number;
  seats: SeatMeta[]; 
  heroSeatId: number | null; 
  heroTargetVisualIndex: number;
  onAction?: (seatId: number, action: ActionType) => void; 
  isSetupComplete: boolean;
  smallBlind: number;
  bigBlind: number;
  heroCards?: { holeCard1: string; holeCard2: string };
  onHeroCard1Click?: () => void;
  onHeroCard2Click?: () => void;
  communityCards?: {
    flopCard1: string;
    flopCard2: string;
    flopCard3: string;
    turnCard: string;
    riverCard: string;
  };
  onFlopCard1Click?: () => void;
  onFlopCard2Click?: () => void;
  onFlopCard3Click?: () => void;
  onFlopCardsClick?: () => void;
  onTurnCardClick?: () => void;
  onRiverCardClick?: () => void;
  stackSizes: Record<number, number>;
  onStackChange: (seatId: number, value: string) => void;
  currentPlayerId: number | null;
  foldedPlayerIds: Set<number>;
  currentBetToCall: number;
  currentStreet: Street;
  nextPlayerToActId: number | null;
  onSeatClick: (seatId: number) => void;
  isDebugSwivelModeActive?: boolean;
  playerStreetContributions?: Record<number, number>;
  pot: number;
  lastActionPlayerId?: number | null;
  lastActionType?: string | null;
  lastActionAmount?: number | null;
  isReplayMode?: boolean;
  isInputMode?: boolean;
  autoFoldPlayerIds?: Set<number>;
  straddlePlayerIds?: Set<number>;
  onRaiseAmount?: (seatId: number, amount: number) => void;
  inputModeActions?: Record<number, { action: ActionType; timestamp: number }>;
  seatsWithActions?: Set<number>;
  onBetClick?: (seatId: number) => void; // New prop for clicking on bet amounts
  // Street navigation props for input mode
  inputModeCurrentStreet?: Street;
  onStreetChange?: (street: Street) => void;
  currentStreetFoldedPlayers?: Set<number>; // Players who folded on the current street (should remain visible)
  onUndoLastAction?: () => void;
  onAnalyzeHand?: () => void;
}

// Card dimensions - made smaller to fit above stack counter
const heroCardWidth = 45; // Reduced from 65 to fit in nameplate
const nonHeroCardWidth = 30; // Reduced from 40 to fit in nameplate  
const communityCardWidth = 65; // Keep community cards same size
const heroCardHeight = heroCardWidth * 1.4;
const nonHeroCardHeight = nonHeroCardWidth * 1.4;
const communityCardHeight = communityCardWidth * 1.4;
const chipRadius = 18;                       
const PLAYER_STACK_TANGENTIAL_OFFSET = 60; // NEW: Offset for stack display to the right of the seat

const radialMenuRadius = 93;            
const radialMenuInnerRadius = 58;       
const heroTagHeight = 20;               

const TABLE_EDGE_INSET = 145; // Increased from 127 to make table ~10% narrower
const RIM_STRIP_THICKNESS = 30;
const FELT_INSET_FROM_SVG_EDGE = TABLE_EDGE_INSET + RIM_STRIP_THICKNESS;
const BETTING_LINE_FELT_GAP = 170; // Increased from 140 to 170 to move betting line even closer to center

const SEAT_VISUAL_DEPTH_BEHIND_RIM = 0;
const baseCardOutwardOffsetFromBettingLine = 15; // Reduced from 25 to 15 to move cards further toward center
const aggressiveCornerOutwardShift = 25;
const slightCornerOutwardShift = 10;

const PokerVisual: React.FC<PokerVisualProps> = ({ 
  seatCount,
  seats = [],
  heroSeatId,
  heroTargetVisualIndex,
  onAction,
  isSetupComplete,
  smallBlind,
  bigBlind,
  heroCards,
  onHeroCard1Click,
  onHeroCard2Click,
  communityCards,
  onFlopCard1Click,
  onFlopCard2Click,
  onFlopCard3Click,
  onFlopCardsClick,
  onTurnCardClick,
  onRiverCardClick,
  stackSizes,
  onStackChange,
  currentPlayerId,
  foldedPlayerIds,
  currentBetToCall,
  currentStreet,
  nextPlayerToActId,
  onSeatClick,
  isDebugSwivelModeActive = false,
  playerStreetContributions,
  pot,
  lastActionPlayerId,
  lastActionType,
  lastActionAmount,
  isReplayMode = false,
  isInputMode = false,
  autoFoldPlayerIds,
  straddlePlayerIds,
  onRaiseAmount,
  inputModeActions,
  seatsWithActions,
  onBetClick,
  inputModeCurrentStreet,
  onStreetChange,
  currentStreetFoldedPlayers,
  onUndoLastAction,
  onAnalyzeHand,
}) => {

  const [interactiveRotations, setInteractiveRotations] = useState<Record<number, number>>({});
  const [focusedInputSeatId, setFocusedInputSeatId] = useState<number | null>(null);
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [editableSettings, setEditableSettings] = useState({
    smallBlind: smallBlind,
    bigBlind: bigBlind,
    seatCount: seatCount,
    heroPosition: seats.find(s => s.id === heroSeatId)?.label || 'BTN'
  });
  

  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth);
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  const isMobile = viewportWidth <= 768; // Changed from 600 to catch more mobile devices

  // Calculate adjusted card dimensions based on screen size
  const mobileAdjustedCommunityCardWidth = isMobile ? 65 : communityCardWidth * 1.15;
  const mobileAdjustedCommunityCardHeight = mobileAdjustedCommunityCardWidth * 1.4;
  
  const mobileAdjustedChipRadius = isMobile ? 14 : 12;
  const desktopSizeFactor = isMobile ? 1 : 1.15;
  
  // Use hero card width for the base calculation - we'll adjust per seat later
  const adjustedHeroCardWidth = Math.round(heroCardWidth * desktopSizeFactor);
  const adjustedHeroCardHeight = adjustedHeroCardWidth * 1.4;
  const adjustedNonHeroCardWidth = Math.round(nonHeroCardWidth * desktopSizeFactor);
  const adjustedNonHeroCardHeight = adjustedNonHeroCardWidth * 1.4;

  const chipDenominations = useMemo(() => [
    100000, 50000, 25000, 10000, 5000, 1000, 500, 100, 25, 5, 1
  ], []); 

  const calculateChipStack = useCallback((amount: number): number[] => {
    let remainingAmount = amount;
    const stack: number[] = [];
    if (remainingAmount <= 0) return stack; 
    for (const denom of chipDenominations) {
      const count = Math.floor(remainingAmount / denom);
      if (count > 0) {
        for (let i = 0; i < count; i++) {
          stack.push(denom);
        }
        remainingAmount -= count * denom;
      }
      if (remainingAmount <= 0) break;
    }
    return stack;
  }, [chipDenominations]);
  
  // Use currentPlayerId for menu visibility in input mode, otherwise use local state
  const showMenuForSeatId = isInputMode ? currentPlayerId : null;

  const svgContainerRef = useRef<SVGSVGElement>(null);

  const { S_WIDTH, S_HEIGHT, strokeW } = useMemo(() => {
    if (isMobile) {
      const width = 900; 
      const height = 1100; // Reverted back to 1100
      const sW = 2; 
      return { S_WIDTH: width, S_HEIGHT: height, strokeW: sW };
    } else {
      const width = 1280;
      const height = 920; // Reverted back to 920
      const sW = 2.3;
      return { S_WIDTH: width, S_HEIGHT: height, strokeW: sW };
    }
  }, [isMobile]);

  const calculatedPoints = useMemo(() => {
    const viewboxWidth = S_WIDTH;
    const viewboxHeight = S_HEIGHT;
    const viewboxCenterX = viewboxWidth / 2;
    const viewboxCenterY = viewboxHeight / 2;
    
    const mobileBettingLineFeltGap = isMobile ? 180 : BETTING_LINE_FELT_GAP; // Increased mobile gap to move even closer to center
    const correctBettingLineOffset = FELT_INSET_FROM_SVG_EDGE + mobileBettingLineFeltGap;

    const calculatedSeatPoints: ({ x: number; y: number } | null)[] = new Array(seats.length).fill(null);
    const calculatedCardPoints: ({ x: number; y: number } | null)[] = new Array(seats.length).fill(null);
    const calculatedStackDisplayPoints: ({ x: number; y: number } | null)[] = new Array(seats.length).fill(null);
    const calculatedBettingChipPoints: ({ x: number; y: number } | null)[] = new Array(seats.length).fill(null);
    const calculatedBettingLinePoints: ({ x: number; y: number } | null)[] = []; // Initialize if needed, or remove if not used.

    const rimEffectiveWidth = viewboxWidth - 2 * TABLE_EDGE_INSET;
    const rimEffectiveHeight = viewboxHeight - 2 * TABLE_EDGE_INSET;
    const rimPathCornerRadius = rimEffectiveHeight / 2;
    const rimPathStraightLength = Math.max(0, rimEffectiveWidth - 2 * rimPathCornerRadius);
    const rim_p1x = viewboxCenterX - rimPathStraightLength / 2;
    const rim_p1y = TABLE_EDGE_INSET;
    const rim_p2x = viewboxCenterX + rimPathStraightLength / 2;
    const rim_p2y = TABLE_EDGE_INSET;
    const rim_p3x = rim_p2x;
    const rim_p3y = viewboxHeight - TABLE_EDGE_INSET;
    const rim_p4x = rim_p1x;
    const rim_p4y = viewboxHeight - TABLE_EDGE_INSET;
    const rimPathData = [
      `M ${rim_p1x.toFixed(3)},${rim_p1y.toFixed(3)}`,
      `L ${rim_p2x.toFixed(3)},${rim_p2y.toFixed(3)}`,
      `A ${rimPathCornerRadius.toFixed(3)},${rimPathCornerRadius.toFixed(3)} 0 0 1 ${rim_p3x.toFixed(3)},${rim_p3y.toFixed(3)}`,
      `L ${rim_p4x.toFixed(3)},${rim_p4y.toFixed(3)}`,
      `A ${rimPathCornerRadius.toFixed(3)},${rimPathCornerRadius.toFixed(3)} 0 0 1 ${rim_p1x.toFixed(3)},${rim_p1y.toFixed(3)}`,
      `Z`
    ].join(" ");

    const feltPathEffectiveWidth = viewboxWidth - 2 * FELT_INSET_FROM_SVG_EDGE;
    const feltPathEffectiveHeight = viewboxHeight - 2 * FELT_INSET_FROM_SVG_EDGE;
    const feltPathCornerRadius = feltPathEffectiveHeight / 2;
    const feltPathStraightLength = Math.max(0, feltPathEffectiveWidth - 2 * feltPathCornerRadius);
    const felt_p1x = viewboxCenterX - feltPathStraightLength / 2;
    const felt_p1y = FELT_INSET_FROM_SVG_EDGE;
    const felt_p2x = viewboxCenterX + feltPathStraightLength / 2;
    const felt_p2y = FELT_INSET_FROM_SVG_EDGE;
    const felt_p3x = felt_p2x;
    const felt_p3y = viewboxHeight - FELT_INSET_FROM_SVG_EDGE;
    const felt_p4x = felt_p1x;
    const felt_p4y = viewboxHeight - FELT_INSET_FROM_SVG_EDGE;
    const feltPathData = [
      `M ${felt_p1x.toFixed(3)},${felt_p1y.toFixed(3)}`,
      `L ${felt_p2x.toFixed(3)},${felt_p2y.toFixed(3)}`,
      `A ${feltPathCornerRadius.toFixed(3)},${feltPathCornerRadius.toFixed(3)} 0 0 1 ${felt_p3x.toFixed(3)},${felt_p3y.toFixed(3)}`,
      `L ${felt_p4x.toFixed(3)},${felt_p4y.toFixed(3)}`,
      `A ${feltPathCornerRadius.toFixed(3)},${feltPathCornerRadius.toFixed(3)} 0 0 1 ${felt_p1x.toFixed(3)},${felt_p1y.toFixed(3)}`,
      `Z`
    ].join(" ");
    
    let bettingLinePathData = "";
    const bettingLinePathEffectiveWidth = Math.max(0, viewboxWidth - 2 * correctBettingLineOffset);
    const bettingLinePathEffectiveHeight = Math.max(0, viewboxHeight - 2 * correctBettingLineOffset);
    if (bettingLinePathEffectiveWidth > 0 && bettingLinePathEffectiveHeight > 0) {
      const bettingLinePathCornerRadius = bettingLinePathEffectiveHeight / 2;
      const bettingLinePathStraightLength = Math.max(0, bettingLinePathEffectiveWidth - 2 * bettingLinePathCornerRadius);
      const bl_p1x = viewboxCenterX - bettingLinePathStraightLength / 2;
      const bl_p1y = correctBettingLineOffset;
      const bl_p2x = viewboxCenterX + bettingLinePathStraightLength / 2;
      const bl_p2y = correctBettingLineOffset;
      const bl_p3x = bl_p2x;
      const bl_p3y = viewboxHeight - correctBettingLineOffset;
      const bl_p4x = bl_p1x;
      const bl_p4y = viewboxHeight - correctBettingLineOffset;
      bettingLinePathData = [
        `M ${bl_p1x.toFixed(3)},${bl_p1y.toFixed(3)}`,
        `L ${bl_p2x.toFixed(3)},${bl_p2y.toFixed(3)}`,
        `A ${bettingLinePathCornerRadius.toFixed(3)},${bettingLinePathCornerRadius.toFixed(3)} 0 0 1 ${bl_p3x.toFixed(3)},${bl_p3y.toFixed(3)}`,
        `L ${bl_p4x.toFixed(3)},${bl_p4y.toFixed(3)}`,
        `A ${bettingLinePathCornerRadius.toFixed(3)},${bettingLinePathCornerRadius.toFixed(3)} 0 0 1 ${bl_p1x.toFixed(3)},${bl_p1y.toFixed(3)}`,
        `Z`
      ].join(" ");
    }

    const seatPlacementAxisA = rimEffectiveWidth / 2;
    const seatPlacementAxisB = rimEffectiveHeight / 2;

    if (seats.length > 0) {
      // Calculate rim perimeter for proper seat distribution
      const rimPerimeter = 2 * Math.PI * rimPathCornerRadius + 2 * rimPathStraightLength;
      const seatSpacing = rimPerimeter / seats.length;
      
      // Handle hero positioning and angle offset
      const visualBaseStartIndexRatio = 3 / 4; 
      let angleOffset = 0; 
      if (isSetupComplete && heroSeatId !== null) {
        const heroLogicalIndex = seats.findIndex(s => s.id === heroSeatId);
        if (heroLogicalIndex !== -1) {
          if (isMobile) {
            // Mobile: Place hero at center bottom and distribute others around
            // Calculate where center bottom is in the perimeter
            const topStraightLength = rimPathStraightLength;
            const rightArcLength = Math.PI * rimPathCornerRadius;
            const bottomStraightLength = rimPathStraightLength;
            
            // Center bottom is halfway along the bottom straight edge
            const centerBottomDistance = topStraightLength + rightArcLength + (bottomStraightLength / 2);
            
            // Calculate offset to place hero at center bottom
            const baseDistanceForOffsetCalc = visualBaseStartIndexRatio * rimPerimeter;
            const actualHeroDistance = baseDistanceForOffsetCalc + heroLogicalIndex * seatSpacing;
            const targetHeroDistance = centerBottomDistance;
            angleOffset = (targetHeroDistance - actualHeroDistance) / rimPerimeter * 2 * Math.PI;
          } else {
            // Desktop: Use original logic
            const baseDistanceForOffsetCalc = visualBaseStartIndexRatio * rimPerimeter;
            const targetVisualDistance = baseDistanceForOffsetCalc + heroTargetVisualIndex * seatSpacing;
            const actualHeroDistance = baseDistanceForOffsetCalc + heroLogicalIndex * seatSpacing;
            angleOffset = (targetVisualDistance - actualHeroDistance) / rimPerimeter * 2 * Math.PI;
          }
        }
      }

      seats.forEach((seat, logicalIndex) => {
          // Declare seat position variables
          let seatPointX: number;
          let seatPointY: number;
          
          // Define rim segments for desktop
          const topStraightLength = rimPathStraightLength;
          const bottomStraightLength = rimPathStraightLength;
          
          // Calculate distance along rim perimeter for ALL devices (revert to working approach)
          const baseDistance = visualBaseStartIndexRatio * rimPerimeter + logicalIndex * seatSpacing;
          const adjustedDistance = (baseDistance + (angleOffset / (2 * Math.PI)) * rimPerimeter) % rimPerimeter;
          
          // Determine position on rim based on distance
          if (adjustedDistance <= topStraightLength) {
            // Top straight edge
            const progress = adjustedDistance / topStraightLength;
            seatPointX = rim_p1x + progress * (rim_p2x - rim_p1x);
            seatPointY = rim_p1y;
          } else if (adjustedDistance <= topStraightLength + Math.PI * rimPathCornerRadius) {
            // Right semicircle
            const arcDistance = adjustedDistance - topStraightLength;
            const angle = (arcDistance / (Math.PI * rimPathCornerRadius)) * Math.PI - Math.PI/2; // Start from top
            seatPointX = rim_p2x + rimPathCornerRadius * Math.cos(angle);
            seatPointY = viewboxCenterY + rimPathCornerRadius * Math.sin(angle);
          } else if (adjustedDistance <= topStraightLength + Math.PI * rimPathCornerRadius + bottomStraightLength) {
            // Bottom straight edge  
            const straightDistance = adjustedDistance - topStraightLength - Math.PI * rimPathCornerRadius;
            const progress = straightDistance / bottomStraightLength;
            seatPointX = rim_p3x - progress * (rim_p3x - rim_p4x); // Right to left
            seatPointY = rim_p3y;
          } else {
            // Left semicircle
            const arcDistance = adjustedDistance - topStraightLength - Math.PI * rimPathCornerRadius - bottomStraightLength;
            const angle = (arcDistance / (Math.PI * rimPathCornerRadius)) * Math.PI + Math.PI/2; // Start from bottom
            seatPointX = rim_p4x + rimPathCornerRadius * Math.cos(angle);
            seatPointY = viewboxCenterY + rimPathCornerRadius * Math.sin(angle);
          }
          
          // Mobile positioning now handled in main if-else block above
          const adjustedSeatPoint = { x: seatPointX, y: seatPointY };
          calculatedSeatPoints[logicalIndex] = adjustedSeatPoint;

          // Calculate card position for this seat using rim-based positioning
          // Determine direction from seat to center for card positioning
          const seatToCenterX = viewboxCenterX - seatPointX;
          const seatToCenterY = viewboxCenterY - seatPointY;
          const seatToCenterDistance = Math.sqrt(seatToCenterX * seatToCenterX + seatToCenterY * seatToCenterY);
          
          // Position cards based on seat position relative to table center
          const cardAngleToCenter = Math.atan2(seatPointY - viewboxCenterY, seatPointX - viewboxCenterX) * 180 / Math.PI;
          const cardIsTopHalf = cardAngleToCenter > -180 && cardAngleToCenter < 0;
          
          // Calculate nameplate position (same logic as in the render section)
          const chipRadius = isMobile ? 42 : 39;
          const panelHeight = isMobile ? 52 : 51;
          const panelCenterY = cardIsTopHalf 
            ? -chipRadius - (panelHeight/2) - 5 // Panel above chip
            : chipRadius + (panelHeight/2) + 5;  // Panel below chip
          
          // Position cards within the nameplate panel area
          const isHeroSeat = logicalIndex === seats.findIndex(s => s.id === heroSeatId);
          const cardHeight = isHeroSeat ? adjustedHeroCardHeight : adjustedNonHeroCardHeight;
          
          const cardOffsetWithinPanel = cardIsTopHalf 
            ? -(panelHeight/2 + cardHeight/2 + 2) // For top half: cards touch top edge of nameplate panel (with tiny gap)
            : (panelHeight/2 + cardHeight/2 + 2);  // For bottom half: cards touch bottom edge of nameplate panel (with tiny gap)
          
          const cardCenterX = seatPointX;
          const cardCenterY = seatPointY + panelCenterY + cardOffsetWithinPanel;
          
          calculatedCardPoints[logicalIndex] = { x: cardCenterX, y: cardCenterY };

          // --- Stack display points (chips) - Tangential to the right of the seat ---
          const vec_center_to_player_x = seatPointX - viewboxCenterX;
          const vec_center_to_player_y = seatPointY - viewboxCenterY;
          const dist_center_to_player = Math.sqrt(
            vec_center_to_player_x * vec_center_to_player_x + 
            vec_center_to_player_y * vec_center_to_player_y
          );
          let stackDisplayX = seatPointX;
          let stackDisplayY = seatPointY;
          if (dist_center_to_player > 0) {
            const norm_c2p_x = vec_center_to_player_x / dist_center_to_player;
            const norm_c2p_y = vec_center_to_player_y / dist_center_to_player;
            const tangent_x = norm_c2p_y;
            const tangent_y = -norm_c2p_x;
            stackDisplayX = seatPointX + tangent_x * PLAYER_STACK_TANGENTIAL_OFFSET;
            stackDisplayY = seatPointY + tangent_y * PLAYER_STACK_TANGENTIAL_OFFSET;
          }
          calculatedStackDisplayPoints[logicalIndex] = { x: stackDisplayX, y: stackDisplayY };
          
          // Calculate betting chip position for this seat using rim-based positioning
          const bettingChipInsetFromRim = 60; // Distance inward from rim for betting chips
          const bettingChipDirectionX = (viewboxCenterX - seatPointX) / Math.sqrt((viewboxCenterX - seatPointX) ** 2 + (viewboxCenterY - seatPointY) ** 2);
          const bettingChipDirectionY = (viewboxCenterY - seatPointY) / Math.sqrt((viewboxCenterX - seatPointX) ** 2 + (viewboxCenterY - seatPointY) ** 2);
          const finalBettingChipX = seatPointX + bettingChipDirectionX * bettingChipInsetFromRim;
          const finalBettingChipY = seatPointY + bettingChipDirectionY * bettingChipInsetFromRim;
          
          calculatedBettingChipPoints[logicalIndex] = { x: finalBettingChipX, y: finalBettingChipY };
          // Betting line points are not calculated per seat
      });
    }

    return {
      seatPoints: calculatedSeatPoints,
      cardPoints: calculatedCardPoints,
      stackDisplayPoints: calculatedStackDisplayPoints,
      bettingLinePoints: calculatedBettingLinePoints, 
      bettingChipPoints: calculatedBettingChipPoints,
      bettingLinePathData: bettingLinePathData,
      feltPathData: feltPathData,
      rimPathData: rimPathData,
      // innerRimDebugPoints: calculatedInnerRimDebugPoints, // REMOVED
    };
  }, [S_WIDTH, S_HEIGHT, strokeW, seats, heroSeatId, heroTargetVisualIndex, isSetupComplete, isMobile]);

  const { 
    seatPoints, cardPoints, stackDisplayPoints,
    bettingChipPoints,
    bettingLinePathData,
    feltPathData, 
    rimPathData,
    // innerRimDebugPoints, // REMOVED
  } = calculatedPoints;

  // Chip styling constants
  const chipStrokeWidth = 0.5; // Thinner stroke for subtlety
  const chipStackOffset = mobileAdjustedChipRadius * 0.25; // Tighter stacking
  const chipColors: { [key: number]: string } = {
    1: '#60a5fa',    // Blue ($1)
    5: '#ef4444',    // Red ($5)
    25: '#22c55e',   // Green ($25)
    100: '#1f2937',  // Black ($100) 
    500: '#a855f7',  // Purple ($500)
    1000: '#f59e0b', // Orange/Yellow ($1,000)
    5000: '#6b7280', // Gray ($5,000)
    10000: '#9f1239', // Burgundy ($10,000) 
    25000: '#67e8f9', // Light Blue ($25,000)
    50000: '#f472b6', // Pink ($50,000)
    100000: '#eab308' // Gold ($100,000)
  };
  const defaultChipColor = '#9ca3af'; 
  const chipStrokeColor = '#4b5563';

  // Mini card styling
  const cardSpacing = 1; // Reduced spacing for smaller cards in nameplate
  const cardFill = '#ffffff'; // White background like community cards
  const cardStroke = '#333333'; // Same border as community cards

  // Chip text styling
  const chipTextFontSize = chipRadius * 0.7;
  const chipTextColor = (bgColor: string): string => {
    const darkColors = ['#1f2937', '#9f1239'];
    return darkColors.includes(bgColor) ? '#ffffff' : '#000000'; 
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showMenuForSeatId !== null && onSeatClick) {
        if (svgContainerRef.current && !svgContainerRef.current.contains(event.target as Node)) {
          onSeatClick(showMenuForSeatId); // Toggle to close
        } else if ((event.target as SVGElement)?.classList?.contains('table-background-path')) {
          onSeatClick(showMenuForSeatId); // Toggle to close
        }
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showMenuForSeatId !== null && onSeatClick) {
        onSeatClick(showMenuForSeatId); // Toggle to close
      }
    };

    if (showMenuForSeatId !== null) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMenuForSeatId]); 

  const handleRadialMenuSelect = (action: ActionType) => {
    if (showMenuForSeatId !== null) {
      // For bet and raise actions in input mode, we need to show bet sizing dialog
      if (isInputMode && (action === 'bet' || action === 'raise')) {
        // Call onRaiseAmount if available to trigger bet sizing dialog
        if (onRaiseAmount) {
          onRaiseAmount(showMenuForSeatId, 0); // Pass 0 as placeholder, actual amount will be set in dialog
        }
        // Don't close menu yet - it will be closed when bet amount is confirmed or cancelled
        return;
      }
      
      // For straddle action, handle it as a preset bet of 2x BB
      if (isInputMode && action === 'straddle') {
        const straddleAmount = bigBlind * 2;
        console.log(`[STRADDLE] Player ${showMenuForSeatId} straddles for $${straddleAmount}`);
        
        // Create a straddle action that looks like a bet but with preset amount
        if (onAction) {
          onAction(showMenuForSeatId, 'straddle');
        }
        return;
      }
      
      // For other actions, proceed normally
      if (onAction) {
        onAction(showMenuForSeatId, action);
      }
    }
  };

  // Adjust panel dimensions based on screen size
  const getResponsiveDimensions = useMemo(() => {
    // On mobile, make nameplates narrower but with bigger text
    const mobileFactor = isMobile ? 0.7 : 1;
    
    return {
      nameplateWidth: isMobile ? 140 : 207, // 180 * 1.15 = 207
      nameplateHeight: isMobile ? 65 : 69,  // 60 * 1.15 = 69
      positionCircleRadius: isMobile ? 25 : 35, // 30 * 1.15 = 34.5, rounded to 35
      fontSize: {
        position: isMobile ? 16 : 21,      // 18 * 1.15 = 20.7, rounded to 21
        playerName: isMobile ? 15 : 18,    // 16 * 1.15 = 18.4, rounded to 18
        stack: isMobile ? 22 : 23          // 20 * 1.15 = 23
      },
      // Add specific mobile padding/margins
      padding: {
        nameplate: isMobile ? 4 : 9 // 8 * 1.15 = 9.2, rounded to 9
      }
    };
  }, [isMobile]);

  // Add this helper function to calculate text scaling
  const getTextSize = (baseSize: number): number => {
    // Ensure text has a minimum readable size regardless of screen
    const minSize = 14;
    const calculatedSize = isMobile ? Math.max(baseSize * 1.1, minSize) : baseSize;
    console.log(`[PokerVisual] isMobile: ${isMobile}, viewport: ${viewportWidth}, baseSize: ${baseSize}, calculatedSize: ${calculatedSize}`);
    return calculatedSize;
  };

  // New state for animated action text
  const [animatedAction, setAnimatedAction] = useState<{ playerId: number; text: string; type: string | null; key: string } | null>(null);

  // Effect to handle incoming last action and trigger animation
  useEffect(() => {
    if (lastActionPlayerId != null && lastActionType != null) {
      let actionText = '';
      const actionType = lastActionType.toLowerCase();
      
      // Create more varied and readable action labels with emojis
      switch (actionType) {
        case 'fold':
          actionText = "✗ FOLD";
          break;
        case 'call':
          if (lastActionAmount != null && lastActionAmount > 0) {
            // Format amount with K/M notation
            let amountText;
            if (lastActionAmount >= 1000000) {
              amountText = `$${(lastActionAmount / 1000000).toFixed(1)}M`;
            } else if (lastActionAmount >= 1000) {
              amountText = `$${(lastActionAmount / 1000).toFixed(0)}K`;
            } else {
              amountText = `$${lastActionAmount}`;
            }
            actionText = `💰 Call ${amountText}`;
          } else {
            actionText = "💰 Call";
          }
          break;
        case 'check':
          actionText = "✓ Check";
          break;
        case 'bet':
          if (lastActionAmount != null && lastActionAmount > 0) {
            let amountText;
            if (lastActionAmount >= 1000000) {
              amountText = `$${(lastActionAmount / 1000000).toFixed(1)}M`;
            } else if (lastActionAmount >= 1000) {
              amountText = `$${(lastActionAmount / 1000).toFixed(0)}K`;
            } else {
              amountText = `$${lastActionAmount}`;
            }
            actionText = `💸 Bet ${amountText}`;
          } else {
            actionText = "💸 BET";
          }
          break;
        case 'raise':
          if (lastActionAmount != null && lastActionAmount > 0) {
            let amountText;
            if (lastActionAmount >= 1000000) {
              amountText = `$${(lastActionAmount / 1000000).toFixed(1)}M`;
            } else if (lastActionAmount >= 1000) {
              amountText = `$${(lastActionAmount / 1000).toFixed(0)}K`;
            } else {
              amountText = `$${lastActionAmount}`;
            }
            actionText = `🚀 Raise to ${amountText}`;
          } else {
            actionText = "🚀 RAISE";
          }
          break;
        case 'straddle':
          if (lastActionAmount != null && lastActionAmount > 0) {
            let amountText;
            if (lastActionAmount >= 1000000) {
              amountText = `$${(lastActionAmount / 1000000).toFixed(1)}M`;
            } else if (lastActionAmount >= 1000) {
              amountText = `$${(lastActionAmount / 1000).toFixed(0)}K`;
            } else {
              amountText = `$${lastActionAmount}`;
            }
            actionText = `⚡ Straddle ${amountText}`;
          } else {
            actionText = "⚡ STRADDLE";
          }
          break;
        default:
          // Fallback for any other action types
          actionText = lastActionType.toUpperCase();
          if (lastActionAmount != null && lastActionAmount > 0) {
            let amountText;
            if (lastActionAmount >= 1000000) {
              amountText = `$${(lastActionAmount / 1000000).toFixed(1)}M`;
            } else if (lastActionAmount >= 1000) {
              amountText = `$${(lastActionAmount / 1000).toFixed(0)}K`;
            } else {
              amountText = `$${lastActionAmount}`;
            }
            actionText += ` ${amountText}`;
          }
          break;
      }
      
      setAnimatedAction({ 
        playerId: lastActionPlayerId, 
        text: actionText, 
        type: lastActionType, // Store the action type
        key: `${lastActionPlayerId}-${lastActionType}-${lastActionAmount}-${Date.now()}` 
      });
    } else {
      setAnimatedAction(null);
    }
  }, [lastActionPlayerId, lastActionType, lastActionAmount]);

  // Helper function to get action box color
  const getActionBoxColor = (actionType: string | null): string => {
    if (!actionType) return '#4A5568'; // Default dark gray
    switch (actionType.toLowerCase()) {
      case 'fold':
        return '#C53030'; // Red for fold
      case 'call':
        return '#2F855A'; // Green for call
      case 'check':
        return '#4A5568'; // Dark Gray for check
      case 'bet':
      case 'raise':
        return '#F59E0B'; // Amber/Orange for bet/raise
      case 'straddle':
        return '#9333EA'; // Purple for straddle
      default:
        return '#4A5568'; // Default
    }
  };

  // Available positions based on seat count
  const getAvailablePositions = () => {
    const ALL_POSITIONS = [
      { value: 'BB', label: 'BB', minPlayers: 2 },
      { value: 'SB', label: 'SB', minPlayers: 2 },
      { value: 'BTN', label: 'BTN', minPlayers: 3 },
      { value: 'CO', label: 'CO', minPlayers: 4 },
      { value: 'HJ', label: 'HJ', minPlayers: 7 },
      { value: 'MP', label: 'MP', minPlayers: 6 },
      { value: 'UTG+1', label: 'UTG+1', minPlayers: 7 },
      { value: 'UTG', label: 'UTG', minPlayers: 4 },
    ];
    return ALL_POSITIONS.filter(pos => pos.minPlayers <= editableSettings.seatCount);
  };

  const handleSettingsChange = (field: string, value: any) => {
    setEditableSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = () => {
    // TODO: Implement save functionality - pass settings back to parent
    console.log('Saving settings:', editableSettings);
    setShowSettingsModal(false);
  };

  const handleCancelSettings = () => {
    // Reset to original values
    setEditableSettings({
      smallBlind: smallBlind,
      bigBlind: bigBlind,
      seatCount: seatCount,
      heroPosition: seats.find(s => s.id === heroSeatId)?.label || 'BTN'
    });
    setShowSettingsModal(false);
  };

  // Update editable settings when props change
  useEffect(() => {
    setEditableSettings({
      smallBlind: smallBlind,
      bigBlind: bigBlind,
      seatCount: seatCount,
      heroPosition: seats.find(s => s.id === heroSeatId)?.label || 'BTN'
    });
  }, [smallBlind, bigBlind, seatCount, heroSeatId, seats]);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <svg
        ref={svgContainerRef}
        width="100%" 
        height="100%" 
        viewBox={`0 -50 ${S_WIDTH} ${S_HEIGHT + 150}`}
        preserveAspectRatio="xMidYMid meet" 
        xmlns="http://www.w3.org/2000/svg"
        xmlnsXlink="http://www.w3.org/1999/xlink"
        style={{ 
          maxWidth: '100%', 
          maxHeight: '100%', // Changed from isMobile ? '100vh' : '100%'
          minHeight: isMobile ? 'auto' : '500px', // Changed mobile from '100vh' to 'auto'
          touchAction: 'manipulation', 
          height: 'auto' // Changed from isMobile ? '100vh' : 'auto'
        }}
      >
            <defs>        <radialGradient id="cushGrad" cy="35%" r="70%">          <stop offset="0%"   stopColor="#414c72"/>          <stop offset="100%" stopColor="#2e3550"/>        </radialGradient>        <radialGradient id="rimGrad" cx="50%" cy="50%" r="70%">          <stop offset="0%"   stopColor="#555555"/>          <stop offset="100%" stopColor="#222222"/>        </radialGradient>        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">          <feDropShadow dx="0" dy="2" stdDeviation="2"                        floodColor="#000" floodOpacity="0.35"/>        </filter>                {/* Add new glossy panel gradient */}        <linearGradient id="panelGradient" x1="0" y1="0" x2="0" y2="1">          <stop offset="0%" stopColor="#0f1d2d"/>          <stop offset="100%" stopColor="#2a3d59"/>        </linearGradient>                {/* Add new badge chip gradients */}        <radialGradient id="chipGradientSB" cx="50%" cy="50%" r="50%">          <stop offset="0%" stopColor="#3d8fff"/>          <stop offset="100%" stopColor="#003366"/>        </radialGradient>                <radialGradient id="chipGradientBB" cx="50%" cy="50%" r="50%">          <stop offset="0%" stopColor="#ffcc33"/>          <stop offset="100%" stopColor="#cc9900"/>        </radialGradient>                <radialGradient id="chipGradientHero" cx="50%" cy="50%" r="50%">          <stop offset="0%" stopColor="#39d353"/>          <stop offset="100%" stopColor="#077232"/>        </radialGradient>                <radialGradient id="chipGradientDefault" cx="50%" cy="50%" r="50%">          <stop offset="0%" stopColor="#666666"/>          <stop offset="100%" stopColor="#333333"/>        </radialGradient>
        
        {/* Pot styling filter */}
        <filter id="potShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#000000" floodOpacity="0.4"/>
        </filter>

        <style type="text/css">
          {`
            .seat .glow { stroke:transparent; }
            .seat.active .glow{
              stroke:#39d353;
              stroke-width:6;
              animation:pulse 1s ease-out infinite;
            }
            
            /* Next player pulse animation */
            .next-player-pulse {
              animation: nextPlayerPulse 1.5s ease-in-out infinite;
            }
            
            @keyframes nextPlayerPulse {
              0%   { filter: drop-shadow(0 0 10px rgba(57, 211, 83, 0.8)); }
              50%  { filter: drop-shadow(0 0 20px rgba(57, 211, 83, 1)); }
              100% { filter: drop-shadow(0 0 10px rgba(57, 211, 83, 0.8)); }
            }
            
            @keyframes pulse{
              0%   { stroke-opacity:1; }
              60%  { stroke-opacity:0; }
              100% { stroke-opacity:0; }
            }

            /* Style for number inputs to hide spinners */
            input[type=number]::-webkit-inner-spin-button, 
            input[type=number]::-webkit-outer-spin-button { 
              -webkit-appearance: none; 
              margin: 0; 
            }

            /* Mobile optimizations */
            @media (max-width: 768px) {
              .nameplate-text {
                font-weight: bold !important;
                text-shadow: 0px 1px 2px rgba(0,0,0,0.8) !important;
              }
              .poker-nameplate {
                filter: drop-shadow(0px 2px 3px rgba(0,0,0,0.5));
              }
            }

            /* Style for Hero Seat */
            .seat.hero-seat .poker-nameplate {
              stroke: #facc15 !important; /* Yellow glow for hero nameplate */
              stroke-width: 3px !important; /* Increased stroke width for better visibility */
              filter: drop-shadow(0 0 8px #facc15);
            }
            .seat.hero-seat .player-name-text {
              fill: #fde047 !important; /* Lighter yellow for hero name text */
            }
            .seat.hero-seat .hero-tag-text {
              font-weight: bold;
              fill: #facc15; /* Yellow for HERO tag */
              text-anchor: middle;
            }
          `}
        </style>

        <filter id="nextPlayerGlow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="5" result="coloredBlur" />
          <feFlood floodColor="#39d353" floodOpacity="1" result="greenColor" />
          <feComposite in="greenColor" in2="coloredBlur" operator="in" result="greenBlur" />
          <feMerge>
            <feMergeNode in="greenBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>

        {/* ADDED DEFS FOR ACTION TEXT STYLING HERE */}
        <linearGradient id="actionTextGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style={{stopColor: '#4A5568', stopOpacity:0.95}} /> {/* Dark Gray */}
          <stop offset="100%" style={{stopColor: '#2D3748', stopOpacity:0.95}} /> {/* Darker Gray */}
        </linearGradient>
        <filter id="actionTextShadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="1" dy="1" stdDeviation="1.5" floodColor="#000000" floodOpacity="0.5"/>
        </filter>
      </defs>

      <g>
        {/* Table Rim Path - Render first */}
        {rimPathData && (
          <path
            d={rimPathData}
            fill="url(#rimGrad)"
            stroke="#1a1a1a" // Darker stroke for rim
            strokeWidth={strokeW * 1.5} // Slightly thicker stroke for rim
          />
        )}

        {/* Table background (Felt) - Use Custom Path, rendered on top of rim */}
        <path 
          d={feltPathData} 
          fill={"#0b4c25"} 
          stroke={"#4b2d1d"} 
          strokeWidth={strokeW} 
          className="table-background-path" 
        />

        {/* Remove Debug Paths */}
        {/* <path d={feltPathData} fill="none" stroke="red" strokeWidth="1" /> */}
        {/* <path d={bettingLinePathData} fill="none" stroke="yellow" strokeWidth="1" /> */}
        {/* {calculatedSeatPoints && calculatedSeatPoints.length > 0 && (
          <path 
            d={`M${calculatedSeatPoints.filter(p => p !== null).map(p => `${p!.x.toFixed(3)},${p!.y.toFixed(3)}`).join(" L")} Z`}
            fill="none"
            stroke="cyan"
            strokeWidth="1"
            strokeDasharray="2 2"
          />
        )} */}

        {/* Betting Line - Render original */}
        {bettingLinePathData && (
           <path 
             d={bettingLinePathData} 
             fill="none" 
             stroke="#ffffff" 
             strokeWidth={strokeW} 
             strokeDasharray={`${8} ${4}`} 
           />
        )}

        {/* Community Cards in the center */}
        {isSetupComplete && (
          <g transform={`translate(${S_WIDTH / 2}, ${S_HEIGHT / 2})`}>
            {/* LOGGING FOR COACHING HOTSPOT */}
            { (() => { console.log('[PokerVisual] Community Cards Area for Coaching: x', S_WIDTH / 2, 'y', S_HEIGHT / 2); return null; })() }
            {/* Instruction text - only show when cards are needed */}
            {((currentStreet === 'flop' && (!communityCards?.flopCard1 || !communityCards?.flopCard2 || !communityCards?.flopCard3)) || 
              (currentStreet === 'turn' && !communityCards?.turnCard) ||
              (currentStreet === 'river' && !communityCards?.riverCard)) && (
              <text 
                x="0" 
                y="-50" 
                textAnchor="middle" 
                dominantBaseline="middle" 
                fill="#e5e7eb" 
                fontSize="14" 
                fontWeight="bold"
              >
                Click cards to select {currentStreet} cards
              </text>
            )}
            
            {/* Flop - 3 cards (only show if on flop, turn, or river) */}
            {(currentStreet === 'flop' || currentStreet === 'turn' || currentStreet === 'river') && (
              <>
                {/* Flop Card 1 */}
                <g 
                  transform="translate(-140, 0)" 
                  onClick={(e) => {
                    e.stopPropagation();
                    // Allow flop card clicks in input mode or when no player turn is active
                    if ((isInputMode || currentPlayerId === null) && onFlopCard1Click) {
                      onFlopCard1Click();
                    } else {
                      console.log('Flop card 1 click ignored - player turn active');
                    }
                  }}
                  style={{ cursor: (isInputMode || currentPlayerId === null) ? 'pointer' : 'default' }}
                >
                  <rect 
                    x={-mobileAdjustedCommunityCardWidth / 2} 
                    y={-mobileAdjustedCommunityCardHeight / 2} 
                    width={mobileAdjustedCommunityCardWidth} 
                    height={mobileAdjustedCommunityCardHeight} 
                    rx={4} 
                    fill={communityCards?.flopCard1 ? "#ffffff" : "#6b7280"} 
                    stroke={communityCards?.flopCard1 ? "#333333" : "#4b5563"} 
                    strokeWidth={1.5} 
                    filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.3))"
                  />
                  {communityCards?.flopCard1 ? (
                    <>
                      <text 
                        x="-10" 
                        y="-17" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill={communityCards.flopCard1[1] === 'h' || communityCards.flopCard1[1] === 'd' ? "#e11d48" : "#1f2937"} 
                        fontSize={mobileAdjustedCommunityCardHeight * 0.32} 
                        fontWeight="bold"
                      >
                        {communityCards.flopCard1[0]}
                      </text>
                      <text 
                        x="10" 
                        y="17" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill={communityCards.flopCard1[1] === 'h' || communityCards.flopCard1[1] === 'd' ? "#e11d48" : "#1f2937"} 
                        fontSize={mobileAdjustedCommunityCardHeight * 0.42} 
                      >
                        {SUIT_SYMBOLS[communityCards.flopCard1[1]]}
                      </text>
                    </>
                  ) : (
                    <g>
                      <text 
                        x="0" 
                        y="0" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill="#e5e7eb" 
                        fontSize={mobileAdjustedCommunityCardHeight * 0.6} 
                        fontWeight="bold"
                      >
                        ?
                      </text>
                    </g>
                  )}
                </g>
                
                {/* Flop Card 2 */}
                <g 
                  transform="translate(-70, 0)" 
                  onClick={(e) => {
                    e.stopPropagation();
                    // Allow flop card clicks in input mode or when no player turn is active
                    if ((isInputMode || currentPlayerId === null) && onFlopCard2Click) {
                      onFlopCard2Click();
                    } else {
                      console.log('Flop card 2 click ignored - player turn active');
                    }
                  }}
                  style={{ cursor: (isInputMode || currentPlayerId === null) ? 'pointer' : 'default' }}
                >
                  <rect 
                    x={-mobileAdjustedCommunityCardWidth / 2} 
                    y={-mobileAdjustedCommunityCardHeight / 2} 
                    width={mobileAdjustedCommunityCardWidth} 
                    height={mobileAdjustedCommunityCardHeight} 
                    rx={4} 
                    fill={communityCards?.flopCard2 ? "#ffffff" : "#6b7280"} 
                    stroke={communityCards?.flopCard2 ? "#333333" : "#4b5563"} 
                    strokeWidth={1.5} 
                    filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.3))"
                  />
                  {communityCards?.flopCard2 ? (
                    <>
                      <text 
                        x="-10" 
                        y="-17" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill={communityCards.flopCard2[1] === 'h' || communityCards.flopCard2[1] === 'd' ? "#e11d48" : "#1f2937"} 
                        fontSize={mobileAdjustedCommunityCardHeight * 0.32} 
                        fontWeight="bold"
                      >
                        {communityCards.flopCard2[0]}
                      </text>
                      <text 
                        x="10" 
                        y="17" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill={communityCards.flopCard2[1] === 'h' || communityCards.flopCard2[1] === 'd' ? "#e11d48" : "#1f2937"} 
                        fontSize={mobileAdjustedCommunityCardHeight * 0.42} 
                      >
                        {SUIT_SYMBOLS[communityCards.flopCard2[1]]}
                      </text>
                    </>
                  ) : (
                    <g>
                      <text 
                        x="0" 
                        y="0" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill="#e5e7eb" 
                        fontSize={mobileAdjustedCommunityCardHeight * 0.6} 
                        fontWeight="bold"
                      >
                        ?
                      </text>
                    </g>
                  )}
                </g>
                
                {/* Flop Card 3 */}
                <g 
                  transform="translate(0, 0)" 
                  onClick={(e) => {
                    e.stopPropagation();
                    // Allow flop card clicks in input mode or when no player turn is active
                    if ((isInputMode || currentPlayerId === null) && onFlopCard3Click) {
                      onFlopCard3Click();
                    } else {
                      console.log('Flop card 3 click ignored - player turn active');
                    }
                  }}
                  style={{ cursor: (isInputMode || currentPlayerId === null) ? 'pointer' : 'default' }}
                >
                  <rect 
                    x={-mobileAdjustedCommunityCardWidth / 2} 
                    y={-mobileAdjustedCommunityCardHeight / 2} 
                    width={mobileAdjustedCommunityCardWidth} 
                    height={mobileAdjustedCommunityCardHeight} 
                    rx={4} 
                    fill={communityCards?.flopCard3 ? "#ffffff" : "#6b7280"} 
                    stroke={communityCards?.flopCard3 ? "#333333" : "#4b5563"} 
                    strokeWidth={1.5} 
                    filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.3))"
                  />
                  {communityCards?.flopCard3 ? (
                    <>
                      <text 
                        x="-10" 
                        y="-17" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill={communityCards.flopCard3[1] === 'h' || communityCards.flopCard3[1] === 'd' ? "#e11d48" : "#1f2937"} 
                        fontSize={mobileAdjustedCommunityCardHeight * 0.32} 
                        fontWeight="bold"
                      >
                        {communityCards.flopCard3[0]}
                      </text>
                      <text 
                        x="10" 
                        y="17" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill={communityCards.flopCard3[1] === 'h' || communityCards.flopCard3[1] === 'd' ? "#e11d48" : "#1f2937"} 
                        fontSize={mobileAdjustedCommunityCardHeight * 0.42} 
                      >
                        {SUIT_SYMBOLS[communityCards.flopCard3[1]]}
                      </text>
                    </>
                  ) : (
                    <g>
                      <text 
                        x="0" 
                        y="0" 
                        textAnchor="middle" 
                        dominantBaseline="middle" 
                        fill="#e5e7eb" 
                        fontSize={mobileAdjustedCommunityCardHeight * 0.6} 
                        fontWeight="bold"
                      >
                        ?
                      </text>
                    </g>
                  )}
                </g>
              </>
            )}
            
            {/* Turn - 4th card (only show if on turn or river) */}
            {(currentStreet === 'turn' || currentStreet === 'river') && (
              <g 
                transform="translate(70, 0)" 
                onClick={(e) => {
                  e.stopPropagation();
                  // Allow turn card clicks in input mode or when no player turn is active
                  if ((isInputMode || currentPlayerId === null) && onTurnCardClick) {
                    onTurnCardClick();
                  } else {
                    console.log('Turn card click ignored - player turn active');
                  }
                }}
                style={{ cursor: (isInputMode || currentPlayerId === null) ? 'pointer' : 'default' }}
              >
                <rect 
                  x={-mobileAdjustedCommunityCardWidth / 2} 
                  y={-mobileAdjustedCommunityCardHeight / 2} 
                  width={mobileAdjustedCommunityCardWidth} 
                  height={mobileAdjustedCommunityCardHeight} 
                  rx={4} 
                  fill={communityCards?.turnCard ? "#ffffff" : "#6b7280"} 
                  stroke={communityCards?.turnCard ? "#333333" : "#4b5563"} 
                  strokeWidth={1.5} 
                  filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.3))"
                />
                {communityCards?.turnCard ? (
                  <>
                    <text 
                      x="-10" 
                      y="-17" 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      fill={communityCards.turnCard[1] === 'h' || communityCards.turnCard[1] === 'd' ? "#e11d48" : "#1f2937"} 
                      fontSize={mobileAdjustedCommunityCardHeight * 0.32} 
                      fontWeight="bold"
                    >
                      {communityCards.turnCard[0]}
                    </text>
                    <text 
                      x="10" 
                      y="17" 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      fill={communityCards.turnCard[1] === 'h' || communityCards.turnCard[1] === 'd' ? "#e11d48" : "#1f2937"} 
                      fontSize={mobileAdjustedCommunityCardHeight * 0.42} 
                    >
                      {SUIT_SYMBOLS[communityCards.turnCard[1]]}
                    </text>
                  </>
                ) : (
                  <g>
                    <text 
                      x="0" 
                      y="0" 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      fill="#e5e7eb" 
                      fontSize={mobileAdjustedCommunityCardHeight * 0.6} 
                      fontWeight="bold"
                    >
                      ?
                    </text>
                  </g>
                )}
              </g>
            )}
            
            {/* River - 5th card (only show if on river) */}
            {currentStreet === 'river' && (
              <g 
                transform="translate(140, 0)" 
                onClick={(e) => {
                  e.stopPropagation();
                  // Allow river card clicks in input mode or when no player turn is active
                  if ((isInputMode || currentPlayerId === null) && onRiverCardClick) {
                    onRiverCardClick();
                  } else {
                    console.log('River card click ignored - player turn active');
                  }
                }}
                style={{ cursor: (isInputMode || currentPlayerId === null) ? 'pointer' : 'default' }}
              >
                <rect 
                  x={-mobileAdjustedCommunityCardWidth / 2} 
                  y={-mobileAdjustedCommunityCardHeight / 2} 
                  width={mobileAdjustedCommunityCardWidth} 
                  height={mobileAdjustedCommunityCardHeight} 
                  rx={4} 
                  fill={communityCards?.riverCard ? "#ffffff" : "#6b7280"} 
                  stroke={communityCards?.riverCard ? "#333333" : "#4b5563"} 
                  strokeWidth={1.5} 
                  filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.3))"
                />
                {communityCards?.riverCard ? (
                  <>
                    <text 
                      x="-10" 
                      y="-17" 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      fill={communityCards.riverCard[1] === 'h' || communityCards.riverCard[1] === 'd' ? "#e11d48" : "#1f2937"} 
                      fontSize={mobileAdjustedCommunityCardHeight * 0.32} 
                      fontWeight="bold"
                    >
                      {communityCards.riverCard[0]}
                    </text>
                    <text 
                      x="10" 
                      y="17" 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      fill={communityCards.riverCard[1] === 'h' || communityCards.riverCard[1] === 'd' ? "#e11d48" : "#1f2937"} 
                      fontSize={mobileAdjustedCommunityCardHeight * 0.42} 
                    >
                      {SUIT_SYMBOLS[communityCards.riverCard[1]]}
                    </text>
                  </>
                ) : (
                  <g>
                    <text 
                      x="0" 
                      y="0" 
                      textAnchor="middle" 
                      dominantBaseline="middle" 
                      fill="#e5e7eb" 
                      fontSize={mobileAdjustedCommunityCardHeight * 0.6} 
                      fontWeight="bold"
                    >
                      ?
                    </text>
                  </g>
                )}
              </g>
            )}
            
            {/* Pot Display - positioned below community cards */}
            <g transform="translate(0, 60)">
              {/* LOGGING FOR COACHING HOTSPOT */}
              { (() => { console.log('[PokerVisual] Pot Area for Coaching: x', S_WIDTH / 2, 'y', S_HEIGHT / 2 + 60); return null; })() }
              
              {/* Modern pot display with background panel */}
              <g>
                {/* Background panel */}
                <rect 
                  x="-60" 
                  y="-18" 
                  width="120" 
                  height="36" 
                  rx="18" 
                  fill="linear-gradient(145deg, rgba(0, 0, 0, 0.9), rgba(30, 30, 30, 0.95))"
                  stroke="#ffce3e"
                  strokeWidth="2"
                  filter="drop-shadow(0 4px 12px rgba(0,0,0,0.4))"
                />
                
                {/* POT label */}
                <text 
                  x="0" 
                  y="-6" 
                  textAnchor="middle" 
                  dominantBaseline="middle" 
                  fill="#ffce3e" 
                  fontWeight="700" 
                  fontSize={isMobile ? "12" : "11"}
                  fontFamily="system-ui, -apple-system, sans-serif"
                  letterSpacing="0.5px"
                >
                  POT
                </text>
                
                {/* Pot amount */}
                <text 
                  x="0" 
                  y="8" 
                  textAnchor="middle" 
                  dominantBaseline="middle" 
                  fill="#ffffff" 
                  fontWeight="900" 
                  fontSize={isMobile ? "18" : "16"}
                  fontFamily="system-ui, -apple-system, sans-serif"
                >
                  ${pot.toLocaleString()}
                </text>
                
                {/* Small pot icon */}
                <text 
                  x="-45" 
                  y="2" 
                  textAnchor="middle" 
                  dominantBaseline="middle" 
                  fontSize={isMobile ? "16" : "14"}
                >
                  💰
                </text>
              </g>
            </g>
          </g>
        )}

        {/* Group for Seats and related elements */}
        <g>
          {seats.map((seat, logicalIndex) => {
            const seatId = seat.id;
            const label = seat.label;
            const isHero = isSetupComplete && seatId === heroSeatId;
            const isCurrentPlayer = isSetupComplete && seatId === currentPlayerId;
            const isFolded = isSetupComplete && (foldedPlayerIds.has(seatId) || (isInputMode && autoFoldPlayerIds?.has(seatId)));
            const currentStack = stackSizes[seatId] ?? 0;
            const seatPoint = seatPoints[logicalIndex];
            const cardPoint = cardPoints[logicalIndex];
            
            // Input mode specific logic
            const isAutoFold = isInputMode && autoFoldPlayerIds?.has(seatId);
            const isActive = isInputMode && !isAutoFold;
            const isActuallyFolded = foldedPlayerIds.has(seatId); // Track actual fold vs auto-fold separately

            // In input mode on post-flop streets, hide players who folded on PREVIOUS streets
            // but keep visible players who folded on the CURRENT street (so they can be reactivated)
            if (isInputMode && inputModeCurrentStreet !== 'preflop' && isActuallyFolded) {
              const foldedOnCurrentStreet = currentStreetFoldedPlayers?.has(seatId) || false;
              if (!foldedOnCurrentStreet) {
                // Player folded on a previous street - hide them completely
                return null;
              }
              // Player folded on current street - keep them visible (grayed out)
            }

            // In input mode on post-flop streets, also hide auto-fold players
            if (isInputMode && inputModeCurrentStreet !== 'preflop' && isAutoFold) {
              return null;
            }

            if (!seatPoint) return null;
            
            {/* LOGGING FOR COACHING HOTSPOT */}
            { (() => { cardPoint && console.log(`[PokerVisual] Seat ${seatId} Card Area for Coaching: x`, cardPoint.x, 'y', cardPoint.y); return null; })() }
            { (() => { seatPoint && console.log(`[PokerVisual] Seat ${seatId} Main Area for Coaching: x`, seatPoint.x, 'y', seatPoint.y); return null; })() }

            const isNextToAct = isSetupComplete && seatId === nextPlayerToActId;
            // Construct className string for the seat group
            const seatGroupClassName = [
              'seat',
              isHero ? 'hero-seat' : '',
              isCurrentPlayer ? 'active-player-seat' : '',
              isFolded ? 'folded-player' : '',
              isNextToAct && !isReplayMode ? 'next-player-pulse' : ''
            ].filter(Boolean).join(' ');

            // Get responsive dimensions for this seat
            const { nameplateWidth, nameplateHeight, positionCircleRadius, fontSize } = getResponsiveDimensions;

            // Debug line from seat to table center
            const tableCenterX = S_WIDTH / 2;
            const tableCenterY = S_HEIGHT / 2;

            if (seatId === 0) {
              console.log(`[PokerVisual Seat 0] isHero: ${isHero}, isFolded: ${isFolded}, isCurrent: ${isCurrentPlayer}`);
              console.log(`  Calculated Points: seat=${JSON.stringify(seatPoint)}, card=${JSON.stringify(cardPoint)}, stack=${JSON.stringify(stackDisplayPoints[logicalIndex])}`);
            }

            return (
              <React.Fragment key={`seat-fragment-${seatId}`}> 
                {/* Debug Line from seat to table center - REMOVED */}
                {/* Debug Dot on Inner Rim in front of seat - REMOVED */}

                <g 
                  transform={`translate(${seatPoint.x}, ${seatPoint.y})`} 
                  onClick={() => {
                    console.log(`[POKER VISUAL] Seat ${seatId} clicked. isInputMode: ${isInputMode}, isFolded: ${isFolded}, isReplayMode: ${isReplayMode}, isHero: ${isHero}`);
                    
                    if (isInputMode) {
                      // Allow clicking on folded players to reactivate them
                      if (isFolded) {
                        console.log(`[POKER VISUAL] Seat ${seatId} is folded - allowing click to reactivate`);
                        onSeatClick(seatId);
                        return;
                      }
                      
                      // Prevent clicking on seats that have already acted (but not folded)
                      // Commented out - allow re-clicking seats to change actions
                      // if (seatsWithActions?.has(seatId)) {
                      //   console.log(`[POKER VISUAL] Seat ${seatId} has already acted - ignoring click`);
                      //   return;
                      // }
                      
                      console.log(`[POKER VISUAL] Calling onSeatClick for seat ${seatId} in input mode`);
                      onSeatClick(seatId);
                    } else if (!isFolded) {
                      // Allow clicking current or next player unless in replay mode and it's not the hero
                      if (!isReplayMode || (isReplayMode && isHero) || seatId === currentPlayerId || seatId === nextPlayerToActId) {
                        onSeatClick(seatId);
                      }
                    }
                  }}
                  style={{ 
                    cursor: isInputMode ? 
                           'pointer' : // Always allow clicking in input mode (unless folded - handled by onClick logic)
                           (!isFolded && (!isReplayMode || (isReplayMode && isHero) || seatId === currentPlayerId || seatId === nextPlayerToActId)) ? 'pointer' : 'default'
                  }} 
                  className={seatGroupClassName} // Use the constructed class name
                >
                  {/* Next Player Indicator */}
                  {(() => {
                    if (!isNextToAct || isFolded) return null; // Only show when this seat is next in input / replay modes

                    // Place the badge at the card area (inside the table, in front of the seat)
                    if (!cardPoint) return null;

                    const indicatorX = cardPoint.x - seatPoint.x;
                    const indicatorY = cardPoint.y - seatPoint.y;

                    return (
                      <g transform={`translate(${indicatorX}, ${indicatorY})`}>
                        <rect
                          x="-28"
                          y="-11"
                          width="56"
                          height="22"
                          rx="11"
                          fill="rgba(57, 211, 83, 0.92)"
                          stroke="#1a9e44"
                          strokeWidth="2"
                        />
                        <text
                          x="0"
                          y="3"
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#000000"
                          fontWeight="bold"
                          fontSize="11"
                        >
                          NEXT
                        </text>
                      </g>
                    );
                  })()}

                  {/* New SVG-based Combined Seat and Nameplate */}
                  {(() => {
                    // Calculate angle to center to determine proper orientation
                    const angleToCenter = Math.atan2(seatPoint.y - (S_HEIGHT/2), seatPoint.x - (S_WIDTH/2)) * 180 / Math.PI;
                    
                    // Determine if this is on the top or bottom half of the table
                    const isTopHalf = angleToCenter > -180 && angleToCenter < 0;
                    
                    // Panel dimensions - make more minimalistic
                    const panelWidth = isMobile ? 85 : 81; // 70 * 1.15 = 80.5, rounded to 81
                    const panelHeight = isMobile ? 52 : 51; // 44 * 1.15 = 50.6, rounded to 51
                    const chipRadius = isMobile ? 42 : 39; // 34 * 1.15 = 39.1, rounded to 39
                    
                    // Position the panel above or below the chip based on table position
                    // Chip stays at (0,0) - the center of the seat
                    const panelCenterY = isTopHalf 
                      ? -chipRadius - (panelHeight/2) - 5 // For top half: Panel above chip with small gap
                      : chipRadius + (panelHeight/2) + 5;  // For bottom half: Panel below chip with small gap
                    
                    // Badge filling gradient based on position
                    const chipGradientId = isHero ? "chipGradientHero" : "chipGradientSB";
                    
                    return (
                      <g>
                        {/* HERO Tag - Appears above the chip and nameplate */}
                        {isHero && (
                          <text
                            className="hero-tag-text" // Applied via .seat.hero-seat .hero-tag-text
                            x={0} 
                            y={isTopHalf ? panelCenterY - (panelHeight/2) - heroTagHeight + 5 : panelCenterY + (panelHeight/2) + heroTagHeight + 5}
                            dominantBaseline={isTopHalf ? "auto" : "hanging"}
                            fontSize={getTextSize(14)}
                          >
                            HERO
                          </text>
                        )}

                        {/* Position chip - always at center (0,0) of the translated group <g> */}
                        <circle 
                          cx={0} 
                          cy={0} 
                          r={chipRadius} 
                          fill={
                            inputModeActions?.[seatId] ? `url(#${chipGradientId})` : // Remove green feedback, use normal gradient
                            seatsWithActions?.has(seatId) ? "rgba(100, 100, 100, 0.5)" : // Dimmed for already acted
                            `url(#${chipGradientId})`
                          }
                          stroke={
                            inputModeActions?.[seatId] ? (isHero ? "#1a9e44" : "#99caff") : // Use normal stroke colors instead of green
                            seatsWithActions?.has(seatId) ? "#888888" : // Gray stroke for already acted
                            isFolded ? "#444444" : 
                            isHero ? "#1a9e44" : 
                            (!isAutoFold && isInputMode) ? "#ffce3e" : // Yellow only for non-auto-fold seats in input mode
                            "#99caff"
                          } 
                          strokeWidth={
                            inputModeActions?.[seatId] ? 3 : // Stroke width for feedback state
                            isHero ? 5 : 
                            (!isAutoFold && isInputMode) ? 4 : // Thicker stroke only for non-auto-fold seats
                            3
                          }
                          opacity={
                            inputModeActions?.[seatId] ? 1 : // Full opacity for feedback state
                            seatsWithActions?.has(seatId) ? 0.6 : // Reduced opacity for already acted
                            isAutoFold && isInputMode ? 0.4 : // Dim auto-fold seats in input mode
                            1
                          }
                          className={isCurrentPlayer ? "active-player-seat position-chip" : "position-chip"}
                        />
                        
                        {/* Position Label */}
                        <text 
                          x={0} 
                          y={7} 
                          fontFamily="Segoe UI, Arial" 
                          fontSize={isMobile ? 26 : 23} // 20 * 1.15 = 23
                          fill="#ffffff"
                          fontWeight="bold" 
                          textAnchor="middle"
                          className={isFolded ? "folded-text nameplate-text" : "nameplate-text"}
                        >
                          {label}
                        </text>
                        
                        {/* "Already acted" indicator for seats that have taken an action */}
                        {isInputMode && seatsWithActions?.has(seatId) && !inputModeActions?.[seatId] && (
                          <>
                            <circle
                              cx={chipRadius * 0.6}
                              cy={chipRadius * 0.6}
                              r={10}
                              fill="rgba(100, 100, 100, 0.9)"
                              stroke="#ffffff"
                              strokeWidth={2}
                            />
                            <text
                              x={chipRadius * 0.6}
                              y={chipRadius * 0.6 + 3}
                              fontSize={8}
                              fill="#ffffff"
                              fontWeight="bold"
                              textAnchor="middle"
                              dominantBaseline="middle"
                            >
                              ✓
                            </text>
                          </>
                        )}
                        
                        {/* Nameplate panel - positioned relative to chip */}
                        <g transform={`translate(0, ${panelCenterY})`}>
                          <rect 
                            x={-panelWidth/2} 
                            y={-panelHeight/2} 
                            width={panelWidth} 
                            height={panelHeight} 
                            rx={10} 
                            ry={10}
                            className="poker-nameplate" // Class for hero highlighting via CSS
                            fill={
                              isHero && !isFolded ? "rgba(30, 50, 70, 0.9)" : // Distinct fill for Hero (darker, slightly desaturated blue)
                              isFolded ? "rgba(30, 41, 59, 0.8)" : 
                              "rgba(15, 23, 42, 0.85)"
                            } 
                            stroke={
                              isHero && !isFolded ? "#60a5fa" : // Brighter blue stroke for Hero
                              isFolded ? "#444444" : 
                              "#5d87aa"
                            } 
                            strokeWidth={isHero && !isFolded ? 1.5 : 1} // Slightly thicker stroke for Hero nameplate
                            filter="drop-shadow(0px 2px 2px rgba(0,0,0,0.3))" 
                          />
                          
                          {/* Stack Amount - now handled by input field only */}
                          
                          {/* Input for stack editing */}
                          <foreignObject
                            x={-panelWidth/2 + 5} // Add small padding
                            y={-15}
                            width={panelWidth - 10}
                            height={30}
                          >
                            <div style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <div style={{
                                width: '100%',
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                position: 'relative'
                              }}>
                                {/* Dollar sign prefix */}
                                <span style={{
                                  position: 'absolute',
                                  left: '8px',
                                  color: isHero ? "#fde047" : "#10b981",
                                  fontSize: isMobile ? '16px' : '14px',
                                  fontWeight: '900',
                                  fontFamily: 'system-ui, -apple-system, sans-serif',
                                  pointerEvents: 'none',
                                  zIndex: 1
                                }}>
                                  $
                                </span>
                                <input 
                                  type="number"
                                  value={currentStack} 
                                  onChange={(e) => onStackChange(seatId, e.target.value)}
                                  onFocus={(event) => {
                                    event.target.select();
                                    setFocusedInputSeatId(seatId);
                                  }}
                                  onBlur={() => setFocusedInputSeatId(null)}
                                  onClick={(e) => e.stopPropagation()} // Prevent triggering seat click
                                  onMouseDown={(e) => e.stopPropagation()} // Prevent triggering seat click
                                  style={{
                                    background: focusedInputSeatId === seatId 
                                      ? 'linear-gradient(145deg, rgba(0, 0, 0, 0.95), rgba(30, 30, 30, 0.9))' 
                                      : 'linear-gradient(145deg, rgba(0, 0, 0, 0.8), rgba(20, 20, 20, 0.85))',
                                    border: focusedInputSeatId === seatId 
                                      ? '2px solid #ffce3e' 
                                      : '1px solid rgba(255, 255, 255, 0.3)',
                                    borderRadius: '8px',
                                    width: '100%',
                                    height: '100%',
                                    textAlign: 'center' as const,
                                    fontSize: isMobile ? '18px' : '16px', 
                                    fontWeight: '900',
                                    fontFamily: 'system-ui, -apple-system, sans-serif',
                                    appearance: 'none' as const,
                                    WebkitAppearance: 'none' as const,
                                    MozAppearance: 'textfield' as const,
                                    margin: 0,
                                    padding: '6px 8px 6px 24px', // Left padding for dollar sign
                                    outline: 'none',
                                    cursor: 'text',
                                    color: focusedInputSeatId === seatId 
                                      ? (isHero ? "#fde047" : "#ffffff") 
                                      : (isHero ? "#fde047" : "#10b981"),
                                    pointerEvents: 'auto' as const,
                                    boxShadow: focusedInputSeatId === seatId 
                                      ? '0 0 0 3px rgba(255, 206, 62, 0.3), 0 4px 12px rgba(0,0,0,0.4)' 
                                      : '0 2px 8px rgba(0,0,0,0.3)',
                                    backdropFilter: 'blur(4px)',
                                    transition: 'all 0.2s ease-in-out',
                                  }}
                                />
                              </div>
                            </div>
                          </foreignObject>
                        </g>
                        
                        {/* Folded player crossed-out effect */}
                        {isFolded && (
                          <g>
                            {/* Visual effect removed - folded state is clear from missing cards and dimmed appearance */}
                          </g>
                        )}
                      </g>
                    );
                  })()}

                  {/* Mini Cards */}
                  {cardPoint && !isFolded && seatPoint && (
                    (() => {
                    // Thresholds for rotation logic - defined locally as they are specific to rotation zones
                    const yRotationThreshold = 20; 
                    const xRotationThreshold = 20;
                    
                    const showHeroCardsFaceUp = isHero && (isReplayMode || isInputMode) && heroCards; // Show hero cards in both replay mode and input mode

                    // Define the axes for the BASE card path, used for rotation zone detection
                    // These should match the base positioning logic in useMemo
                    const localCorrectBettingLineOffset = FELT_INSET_FROM_SVG_EDGE + BETTING_LINE_FELT_GAP;
                    // baseCardOutwardOffsetFromBettingLine is a const at component scope, use its value directly
                    const baseCardInsetForRotation = localCorrectBettingLineOffset - baseCardOutwardOffsetFromBettingLine; 
                    const cardEllipseAxisA_forRotation = (S_WIDTH/2) - baseCardInsetForRotation;
                    const cardEllipseAxisB_forRotation = (S_HEIGHT/2) - baseCardInsetForRotation;

                    const cardAngleRad = Math.atan2(cardPoint.y - (S_HEIGHT / 2), cardPoint.x - (S_WIDTH / 2));
                    let finalRotationDeg = 0; // Default to 0, will be overridden by zonal logic for non-mobile
                    let isCornerCard = false; // Flag to identify if the card is a corner card

                    // For mobile, make all cards face top of screen (0 degrees)
                    if (isMobile) {
                      finalRotationDeg = 0; // Cards straight up at 0 degrees
                    } 
                    // Calculate rotations for non-mobile
                    else {
                      const isNearTopEdge = Math.abs(cardPoint.y - ((S_HEIGHT / 2) - cardEllipseAxisB_forRotation)) < yRotationThreshold;
                      const isNearBottomEdge = Math.abs(cardPoint.y - ((S_HEIGHT / 2) + cardEllipseAxisB_forRotation)) < yRotationThreshold;
                      const isNearLeftEdge = Math.abs(cardPoint.x - ((S_WIDTH / 2) - cardEllipseAxisA_forRotation)) < xRotationThreshold;
                      const isNearRightEdge = Math.abs(cardPoint.x - ((S_WIDTH / 2) + cardEllipseAxisA_forRotation)) < xRotationThreshold;

                      if (isNearTopEdge || isNearBottomEdge) {
                          finalRotationDeg = 0; 
                      } else if (isNearRightEdge) {
                          finalRotationDeg = 90; 
                      } else if (isNearLeftEdge) {
                          finalRotationDeg = 90; 
                      } else {
                          isCornerCard = true; // This is a corner card
                          // It's a corner - Reset to 45-degree bevels as baseline for debug mode
                          let baselineCornerRotation = 0;
                          if (cardPoint.y < (S_HEIGHT / 2)) { // Top half of the table
                              if (cardPoint.x > (S_WIDTH / 2)) { // Top-Right corner
                                  baselineCornerRotation = 45; 
                              } else { // Top-Left corner
                                  baselineCornerRotation = 135; 
                              }
                          } else { // Bottom half of the table
                              if (cardPoint.x > (S_WIDTH / 2)) { // Bottom-Right corner
                                  baselineCornerRotation = -45; 
                              } else { // Bottom-Left corner
                                  baselineCornerRotation = -135;
                              }
                          }
                          // If in debug swivel mode and an interactive rotation exists, use it
                          if (isDebugSwivelModeActive && interactiveRotations[logicalIndex] !== undefined) {
                              finalRotationDeg = interactiveRotations[logicalIndex];
                          } else {
                              finalRotationDeg = baselineCornerRotation;
                          }
                      }
                    }

                    const handleCardSwivel = () => {
                        if (isDebugSwivelModeActive && isCornerCard) {
                            const currentAngle = interactiveRotations[logicalIndex] !== undefined 
                                ? interactiveRotations[logicalIndex] 
                                : finalRotationDeg; // Fallback to current display if not yet interacted
                            
                            let newAngle = (currentAngle + 5);
                            if (newAngle >= 360) newAngle -= 360; // Keep within 0-359 range

                            setInteractiveRotations(prev => ({
                                ...prev,
                                [logicalIndex]: newAngle
                            }));
                        }
                    };

                    return (
                      <motion.g 
                        key={`cards-${logicalIndex}-${heroSeatId ?? 'initial'}`}
                        initial={{ x: S_WIDTH / 2 - seatPoint.x, y: S_HEIGHT / 2 - seatPoint.y, opacity: 0, scale: 0.5 }} 
                        animate={{ 
                          x: cardPoint.x - seatPoint.x, 
                          y: cardPoint.y - seatPoint.y,
                          opacity: isSetupComplete ? 1 : 0, 
                          scale: isSetupComplete ? 1 : 0.5
                        }}
                        transition={{ delay: isSetupComplete ? ((logicalIndex - (seats.findIndex(s => s.label === 'SB') ?? 0) + seats.length) % seats.length) * 0.1 : 0, duration: 0.4, ease: "easeOut"}}
                        onClick={handleCardSwivel} // Added onClick handler
                        style={{ cursor: isDebugSwivelModeActive && isCornerCard ? 'pointer' : 'default' }} // Add pointer cursor in debug mode for corners
                      >
                        {/* Force rotation to 0 on mobile devices */}
                        <g transform={`rotate(${0})`}>
                          {(() => {
                            // Use different card sizes for hero vs non-hero
                            const cardWidth = isHero ? adjustedHeroCardWidth : adjustedNonHeroCardWidth;
                            const cardHeight = isHero ? adjustedHeroCardHeight : adjustedNonHeroCardHeight;
                            
                            return (
                              <>
                                {/* Card 1 */}
                                <rect 
                                  x={-(cardWidth + cardSpacing / 2)} 
                                  y={-cardHeight / 2} 
                                  width={cardWidth} 
                                  height={cardHeight} 
                                  rx={4} // Same rounded corners as community cards
                                  fill={showHeroCardsFaceUp ? "#ffffff" : (!isHero ? "#ef4444" : cardFill)} 
                                  stroke={showHeroCardsFaceUp ? "#333333" : cardStroke} 
                                  strokeWidth={1.5} 
                                  filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.3))" // Same shadow as community cards
                                  onClick={isHero && isInputMode ? (e) => {
                                    e.stopPropagation(); // Prevent seat click
                                    onHeroCard1Click?.();
                                  } : undefined}
                                  style={{ 
                                    cursor: isHero && isInputMode ? 'pointer' : 'default',
                                    pointerEvents: isHero && isInputMode ? 'auto' : 'none'
                                  }}
                                />
                                {showHeroCardsFaceUp && heroCards?.holeCard1 ? (
                                  <>
                                    <text 
                                      x={-(cardWidth + cardSpacing / 2) + cardWidth / 2 - 10} 
                                      y={-17} 
                                      textAnchor="middle" 
                                      dominantBaseline="middle" 
                                      fill={heroCards.holeCard1[1] === 'h' || heroCards.holeCard1[1] === 'd' ? "#e11d48" : "#1f2937"} 
                                      fontSize={mobileAdjustedCommunityCardHeight * 0.32} 
                                      fontWeight="bold"
                                    >
                                      {heroCards.holeCard1[0]}
                                    </text>
                                    <text 
                                      x={-(cardWidth + cardSpacing / 2) + cardWidth / 2 + 10} 
                                      y={17} 
                                      textAnchor="middle" 
                                      dominantBaseline="middle" 
                                      fill={heroCards.holeCard1[1] === 'h' || heroCards.holeCard1[1] === 'd' ? "#e11d48" : "#1f2937"} 
                                      fontSize={mobileAdjustedCommunityCardHeight * 0.42} 
                                    >
                                      {SUIT_SYMBOLS[heroCards.holeCard1[1]]}
                                    </text>
                                  </>
                                ) : (
                                  <text 
                                    x={-(cardWidth + cardSpacing / 2) + cardWidth * 0.5} 
                                    y={0} 
                                    textAnchor="middle" 
                                    dominantBaseline="middle" 
                                    fill="#f3f4f6" 
                                    fontSize={mobileAdjustedCommunityCardHeight * 0.6} 
                                    fontWeight="bold"
                                  >
                                    ?
                                  </text>
                                )}

                                {/* Larger invisible clickable area for Card 1 */}
                                {isHero && isInputMode && (
                                  <rect 
                                    x={-(cardWidth + cardSpacing / 2) - 8} 
                                    y={-cardHeight / 2 - 8} 
                                    width={cardWidth + 16} 
                                    height={cardHeight + 16} 
                                    fill="transparent"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent seat click
                                      onHeroCard1Click?.();
                                    }}
                                    style={{ 
                                      cursor: 'pointer',
                                      pointerEvents: 'auto'
                                    }}
                                  />
                                )}

                                {/* Card 2 */}
                                <rect 
                                  x={cardSpacing / 2} 
                                  y={-cardHeight / 2} 
                                  width={cardWidth} 
                                  height={cardHeight} 
                                  rx={4} // Same rounded corners as community cards
                                  fill={showHeroCardsFaceUp ? "#ffffff" : (!isHero ? "#ef4444" : cardFill)} 
                                  stroke={showHeroCardsFaceUp ? "#333333" : cardStroke} 
                                  strokeWidth={1.5}
                                  filter="drop-shadow(1px 1px 2px rgba(0,0,0,0.3))" // Same shadow as community cards
                                  onClick={isHero && isInputMode ? (e) => {
                                    e.stopPropagation(); // Prevent seat click
                                    onHeroCard2Click?.();
                                  } : undefined}
                                  style={{ 
                                    cursor: isHero && isInputMode ? 'pointer' : 'default',
                                    pointerEvents: isHero && isInputMode ? 'auto' : 'none'
                                  }}
                                />
                                {showHeroCardsFaceUp && heroCards?.holeCard2 ? (
                                  <>
                                    <text 
                                      x={(cardSpacing / 2) + cardWidth / 2 - 10} 
                                      y={-17} 
                                      textAnchor="middle" 
                                      dominantBaseline="middle" 
                                      fill={heroCards.holeCard2[1] === 'h' || heroCards.holeCard2[1] === 'd' ? "#e11d48" : "#1f2937"} 
                                      fontSize={mobileAdjustedCommunityCardHeight * 0.32} 
                                      fontWeight="bold"
                                    >
                                      {heroCards.holeCard2[0]}
                                    </text>
                                    <text 
                                      x={(cardSpacing / 2) + cardWidth / 2 + 10} 
                                      y={17} 
                                      textAnchor="middle" 
                                      dominantBaseline="middle" 
                                      fill={heroCards.holeCard2[1] === 'h' || heroCards.holeCard2[1] === 'd' ? "#e11d48" : "#1f2937"} 
                                      fontSize={mobileAdjustedCommunityCardHeight * 0.42} 
                                    >
                                      {SUIT_SYMBOLS[heroCards.holeCard2[1]]}
                                    </text>
                                  </>
                                ) : (
                                  <text 
                                    x={(cardSpacing / 2) + cardWidth * 0.5} 
                                    y={0} 
                                    textAnchor="middle" 
                                    dominantBaseline="middle" 
                                    fill="#f3f4f6" 
                                    fontSize={mobileAdjustedCommunityCardHeight * 0.6} 
                                    fontWeight="bold"
                                  >
                                    ?
                                  </text>
                                )}

                                {/* Larger invisible clickable area for Card 2 */}
                                {isHero && isInputMode && (
                                  <rect 
                                    x={cardSpacing / 2 - 8} 
                                    y={-cardHeight / 2 - 8} 
                                    width={cardWidth + 16} 
                                    height={cardHeight + 16} 
                                    fill="transparent"
                                    onClick={(e) => {
                                      e.stopPropagation(); // Prevent seat click
                                      onHeroCard2Click?.();
                                    }}
                                    style={{ 
                                      cursor: 'pointer',
                                      pointerEvents: 'auto'
                                    }}
                                  />
                                )}
                              </>
                            );
                          })()}
                        </g>
                        {isDebugSwivelModeActive && (
                          <text
                            x={0} // Positioned at the card group's center
                            y={(isHero ? adjustedHeroCardHeight : adjustedNonHeroCardHeight) / 2 + 12} // Below the cards
                            fontSize="10"
                            fill="yellow"
                            textAnchor="middle"
                            dominantBaseline="hanging"
                          >
                            {`Angle: ${finalRotationDeg.toFixed(0)}°`}
                          </text>
                        )}
                      </motion.g>
                    );
                  })()
                  )}

                  {/* Chip Pile - DISABLED to reduce clutter */}
                  {/* 
                  {false && isSetupComplete && seatPoint && stackDisplayPoints && stackDisplayPoints[logicalIndex] && !isFolded && (() => {
                    const stackPoint = stackDisplayPoints[logicalIndex]!;
                    const chipStackToRender = calculateChipStack(currentStack).slice(0, 5); // Show fewer chips
                    return (
                      <g transform={`translate(${stackPoint.x - seatPoint.x}, ${stackPoint.y - seatPoint.y})`} opacity="0.8"> 
                        {chipStackToRender.map((chipValue, chipIndex) => (
                          <g key={`stack-chip-${seatId}-${chipIndex}`} transform={`translate(0, ${-chipIndex * chipStackOffset})`}>
                            <PokerChip value={chipValue} size={mobileAdjustedChipRadius * 2} />
                          </g>
                        ))}
                      </g>
                    );
                  })()} 
                  */}

                  {/* Betting Chips - Placed at the betting line */}
                  {isSetupComplete && seatPoint && bettingChipPoints && bettingChipPoints[logicalIndex] && playerStreetContributions && !isFolded && (() => {
                    const bettingPoint = bettingChipPoints[logicalIndex]!;
                    const playerContribution = playerStreetContributions[seatId] || 0;
                    // Show bet chips for contributions > 0, or CHECK label for contributions = 0 with check action
                    if (playerContribution > 0) {
                      const betChipStack = calculateChipStack(playerContribution).slice(0, 4); // Show fewer chips
                      return (
                        <g 
                          transform={`translate(${bettingPoint.x - seatPoint.x}, ${bettingPoint.y - seatPoint.y})`} 
                          opacity="0.8"
                          onClick={isInputMode ? (e) => {
                            e.stopPropagation(); // Prevent seat click
                            console.log(`[POKER VISUAL] Bet clicked for seat ${seatId}, amount: ${playerContribution}`);
                            onBetClick?.(seatId);
                          } : undefined}
                          style={{ 
                            cursor: isInputMode ? 'pointer' : 'default',
                            pointerEvents: isInputMode ? 'auto' : 'none'
                          }}
                        >
                          {betChipStack.map((chipValue, chipIndex) => (
                            <g key={`bet-chip-${seatId}-${chipIndex}`} transform={`translate(0, ${-chipIndex * chipStackOffset})`}>
                              <PokerChip value={chipValue} size={mobileAdjustedChipRadius * 2} />
                            </g>
                          ))}
                          {/* Show total amount above chips - make more prominent since chips are smaller */}
                          <g>
                            {/* Modern background panel for bet amount */}
                            <rect 
                              x="-32" 
                              y={-betChipStack.length * chipStackOffset - 24} 
                              width="64" 
                              height="20" 
                              rx="10" 
                              fill="rgba(0, 0, 0, 0.9)"
                              stroke={isInputMode ? "#ffce3e" : "#ffffff"}
                              strokeWidth="1.5"
                              filter="drop-shadow(0 2px 8px rgba(0,0,0,0.3))"
                            />
                            {/* Bet amount text */}
                            <text 
                              x="0" 
                              y={-betChipStack.length * chipStackOffset - 12} 
                              fontSize="22" 
                              fontWeight="900" 
                              fill={isInputMode ? "#ffce3e" : "#ffffff"} 
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fontFamily="system-ui, -apple-system, sans-serif"
                            >
                              ${playerContribution}
                            </text>
                            {/* Enhanced BET label with its own background in input mode */}
                            {isInputMode && (
                              <g>
                                {/* Background box for BET label */}
                                <rect 
                                  x="-20" 
                                  y={-betChipStack.length * chipStackOffset - 48} 
                                  width="40" 
                                  height="16" 
                                  rx="8" 
                                  fill="rgba(16, 185, 129, 0.95)"
                                  stroke="#059669"
                                  strokeWidth="1.5"
                                  filter="drop-shadow(0 2px 6px rgba(0,0,0,0.4))"
                                />
                                {/* Inner highlight for depth */}
                                <rect 
                                  x="-19" 
                                  y={-betChipStack.length * chipStackOffset - 47} 
                                  width="38" 
                                  height="2" 
                                  rx="1" 
                                  fill="rgba(255, 255, 255, 0.3)"
                                />
                                {/* Action label text - show SB/BB for blinds, BET for actual bets */}
                                <text 
                                  x="0" 
                                  y={-betChipStack.length * chipStackOffset - 40} 
                                  fontSize={getTextSize(isMobile ? 12 : 9)} 
                                  fontWeight="800" 
                                  fill="#ffffff" 
                                  textAnchor="middle"
                                  dominantBaseline="middle"
                                  fontFamily="system-ui, -apple-system, sans-serif"
                                  style={{ 
                                    filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.8))',
                                    letterSpacing: isMobile ? '1.2px' : '0.8px'
                                  }}
                                >
                                  {(() => {
                                    // Find the seat data for this player
                                    const seatData = seats.find(s => s.id === seatId);
                                    const isPreflop = inputModeCurrentStreet === 'preflop' || currentStreet === 'preflop';
                                    
                                    // Show SB/BB labels for blind amounts on preflop
                                    if (isPreflop && seatData) {
                                      if (seatData.label === 'SB' && playerContribution === smallBlind) {
                                        return 'SB';
                                      }
                                      if (seatData.label === 'BB' && playerContribution === bigBlind) {
                                        return 'BB';
                                      }
                                    }
                                    
                                    // For actual betting actions, determine the type based on street contributions
                                    if (playerStreetContributions) {
                                      // Find the highest contribution on this street (excluding this player)
                                      let highestOtherContribution = 0;
                                      Object.entries(playerStreetContributions).forEach(([playerId, contribution]) => {
                                        if (Number(playerId) !== seatId && contribution > highestOtherContribution) {
                                          highestOtherContribution = contribution;
                                        }
                                      });
                                      
                                      // On preflop, consider BB as the baseline "bet to call"
                                      if (isPreflop && highestOtherContribution === 0) {
                                        highestOtherContribution = bigBlind;
                                      }
                                      
                                      if (playerContribution === highestOtherContribution) {
                                        return 'CALL';
                                      } else if (playerContribution > highestOtherContribution) {
                                        return highestOtherContribution > 0 ? 'RAISE' : 'BET';
                                      }
                                    }
                                    
                                    // Default fallback
                                    return 'BET';
                                  })()}
                                </text>
                              </g>
                            )}
                          </g>
                          {/* Enhanced BET label with its own background in input mode */}
                          {isInputMode && (
                            <g>
                              {/* Background box for BET label - adjusted size for mobile */}
                              <rect 
                                x={isMobile ? "-24" : "-20"} 
                                y={-betChipStack.length * chipStackOffset - (isMobile ? 52 : 48)} 
                                width={isMobile ? "48" : "40"} 
                                height={isMobile ? "20" : "16"} 
                                rx="8" 
                                fill="rgba(16, 185, 129, 0.95)"
                                stroke="#059669"
                                strokeWidth="1.5"
                                filter="drop-shadow(0 2px 6px rgba(0,0,0,0.4))"
                              />
                              {/* Inner highlight for depth - adjusted for mobile */}
                              <rect 
                                x={isMobile ? "-23" : "-19"} 
                                y={-betChipStack.length * chipStackOffset - (isMobile ? 51 : 47)} 
                                width={isMobile ? "46" : "38"} 
                                height="2" 
                                rx="1" 
                                fill="rgba(255, 255, 255, 0.3)"
                              />
                              {/* Action label text - show SB/BB for blinds, BET for actual bets */}
                              <text 
                                x="0" 
                                y={-betChipStack.length * chipStackOffset - (isMobile ? 42 : 40)} 
                                fontSize="14" 
                                fontWeight="800" 
                                fill="#ffffff" 
                                textAnchor="middle"
                                dominantBaseline="middle"
                                fontFamily="system-ui, -apple-system, sans-serif"
                                style={{ 
                                  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.8))',
                                  letterSpacing: isMobile ? '1.2px' : '0.8px'
                                }}
                              >
                                {(() => {
                                  // Find the seat data for this player
                                  const seatData = seats.find(s => s.id === seatId);
                                  const isPreflop = inputModeCurrentStreet === 'preflop' || currentStreet === 'preflop';
                                  
                                  // Show SB/BB labels for blind amounts on preflop
                                  if (isPreflop && seatData) {
                                    if (seatData.label === 'SB' && playerContribution === smallBlind) {
                                      return 'SB';
                                    }
                                    if (seatData.label === 'BB' && playerContribution === bigBlind) {
                                      return 'BB';
                                    }
                                  }
                                  
                                  // For actual betting actions, determine the type based on street contributions
                                  if (playerStreetContributions) {
                                    // Find the highest contribution on this street (excluding this player)
                                    let highestOtherContribution = 0;
                                    Object.entries(playerStreetContributions).forEach(([playerId, contribution]) => {
                                      if (Number(playerId) !== seatId && contribution > highestOtherContribution) {
                                        highestOtherContribution = contribution;
                                      }
                                    });
                                    
                                    // On preflop, consider BB as the baseline "bet to call"
                                    if (isPreflop && highestOtherContribution === 0) {
                                      highestOtherContribution = bigBlind;
                                    }
                                    
                                    if (playerContribution === highestOtherContribution) {
                                      return 'CALL';
                                    } else if (playerContribution > highestOtherContribution) {
                                      return highestOtherContribution > 0 ? 'RAISE' : 'BET';
                                    }
                                  }
                                  
                                  // Default fallback
                                  return 'BET';
                                })()}
                              </text>
                            </g>
                          )}
                        </g>
                      );
                    } else if (playerContribution === 0 && isInputMode && inputModeActions?.[seatId]?.action === 'check') {
                      // Show CHECK label using same styling as bet labels
                      return (
                        <g transform={`translate(${bettingPoint.x - seatPoint.x}, ${bettingPoint.y - seatPoint.y})`}>
                          {/* Background box for CHECK label - using blue/gray theme */}
                          <rect 
                            x={isMobile ? "-30" : "-26"} 
                            y={isMobile ? "-54" : "-50"} 
                            width={isMobile ? "60" : "52"} 
                            height={isMobile ? "24" : "20"} 
                            rx="10" 
                            fill="rgba(59, 130, 246, 0.95)"
                            stroke="#3B82F6"
                            strokeWidth="1.5"
                            filter="drop-shadow(0 2px 6px rgba(0,0,0,0.4))"
                          />
                          {/* Inner highlight for depth */}
                          <rect 
                            x={isMobile ? "-29" : "-25"} 
                            y={isMobile ? "-53" : "-49"} 
                            width={isMobile ? "58" : "50"} 
                            height="2" 
                            rx="1" 
                            fill="rgba(255, 255, 255, 0.3)"
                          />
                          {/* CHECK text */}
                          <text 
                            x="0" 
                            y={isMobile ? "-42" : "-40"} 
                            fontSize="14" 
                            fontWeight="800" 
                            fill="#ffffff" 
                            textAnchor="middle"
                            dominantBaseline="middle"
                            fontFamily="system-ui, -apple-system, sans-serif"
                            style={{ 
                              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.8))',
                              letterSpacing: isMobile ? '1.2px' : '0.8px'
                            }}
                          >
                            CHECK
                          </text>
                        </g>
                      );
                    }
                    return null;
                  })()}


                </g> { /* End of main seat group (transformed to seatPoint) */ }

                {/* Animated Action Text for this seat */}
                <AnimatePresence>
                  {animatedAction && animatedAction.playerId === seatId && (
                    <motion.g
                      key={animatedAction.key} 
                      initial={{ opacity: 0, y: seatPoint.y - 100, scale: 0.7 }} 
                      animate={{ opacity: 1, y: seatPoint.y - 120, scale: 1 }} 
                      exit={{ opacity: 0, y: seatPoint.y - 140, scale: 0.7 }}    
                      transition={{ duration: 0.3, ease: "easeOut" }}
                      style={{ pointerEvents: 'none' }} 
                    >
                      { (() => { seatPoint && console.log(`[PokerVisual] Seat ${seatId} Action Text Area for Coaching: x`, seatPoint.x, 'y', seatPoint.y - 120); return null; })() }
                      
                      {/* REMOVED DEFS FROM HERE - MOVED TO MAIN SVG DEFS */}

                      <rect 
                          x={seatPoint.x - (isMobile ? 106 : 85)} 
                          y={isMobile ? -31 : -25} 
                          width={isMobile ? 212 : 170} 
                          height={isMobile ? 62 : 50} 
                          rx={8}      
                          fill={getActionBoxColor(animatedAction.type)} 
                          stroke="#718096" 
                          strokeWidth="1.5"
                          filter="url(#actionTextShadow)" 
                      />
                      <text
                        x={seatPoint.x} 
                        y={0} 
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fill="#F7FAFC" 
                        fontSize={isMobile ? "25px" : "22px"} 
                        fontWeight="bold"
                        paintOrder="stroke"
                        stroke="#1A202C" 
                        strokeWidth="0.7px"
                      >
                        {animatedAction.text}
                      </text>
                    </motion.g>
                  )}
                </AnimatePresence>
              </React.Fragment>
            );
          })}
        </g> { /* End of Group for Seats */ }

        {/* Add transparent overlay when menu is active to block other clicks - ONLY in input mode */}
        {showMenuForSeatId !== null && isInputMode && (
          <rect 
            x={0} 
            y={0} 
            width={S_WIDTH} 
            height={S_HEIGHT} 
            fill="transparent" 
            onClick={() => showMenuForSeatId !== null && onSeatClick?.(showMenuForSeatId)} 
          />
        )}

        {/* Radial Menu (Render on top of everything) - ONLY in input mode */}
        {showMenuForSeatId !== null && isInputMode && (() => {
          const activeSeatIndex = seats.findIndex(s => s.id === showMenuForSeatId);
          const activeSeatData = activeSeatIndex !== -1 ? seats[activeSeatIndex] : null;
          if (activeSeatData && activeSeatIndex !== -1) {
            const point = seatPoints[activeSeatIndex];
            const isHeroSeat = activeSeatData.id === heroSeatId;
            
            let menuOptions: RadialMenuOption[] = []; 
            const playerPosition = activeSeatData.label;
            const currentBet = currentBetToCall; 
            const street = currentStreet;
            
            if (isInputMode) {
              // In input mode, show context-aware menu options
              const playerContribution = playerStreetContributions?.[showMenuForSeatId] || 0;
              
              // For determining betting action, only look at contributions on the CURRENT street
              // Don't include big blind from preflop when checking flop/turn/river actions
              let currentStreetHighestBet = 0;
              if (playerStreetContributions) {
                // Find the highest contribution on the current street only
                Object.values(playerStreetContributions).forEach(contribution => {
                  if (contribution > currentStreetHighestBet) {
                    currentStreetHighestBet = contribution;
                  }
                });
                
                // On fresh streets (no one has bet yet), the highest bet should be 0
                // Only consider it a "bet to call" if someone has actually acted on this street
                if (street !== 'preflop' && currentStreetHighestBet === 0) {
                  currentStreetHighestBet = 0; // Fresh street, no betting action yet
                }
              }
              
              const amountToCall = Math.max(0, currentStreetHighestBet - playerContribution);
              
              menuOptions = [{ action: 'fold', label: 'Fold' }];
              
              // Add straddle option for UTG position on preflop only (only if they haven't contributed yet)
              if (street === 'preflop' && playerPosition === 'UTG' && playerContribution === 0) {
                menuOptions.push({ action: 'straddle', label: 'Straddle' });
              }
              
              if (amountToCall > 0) {
                // There are bets ahead - show call and raise
                menuOptions.push({ action: 'call', label: `Call $${amountToCall}` });
                menuOptions.push({ action: 'raise', label: 'Raise' });
              } else {
                // No bets ahead - show check and bet
                // Special case: if this player has straddled, they should be able to check (their "option")
                if (street === 'preflop' && playerPosition === 'UTG' && playerContribution > 0) {
                  menuOptions.push({ action: 'check', label: 'Check' }); // Straddle option
                  menuOptions.push({ action: 'raise', label: 'Raise' }); // Can still raise
                } else {
                  menuOptions.push({ action: 'check', label: 'Check' });
                  menuOptions.push({ action: 'bet', label: 'Bet' });
                }
              }
            } else {
              // Replay mode logic
              if (currentBet > 0) {
                menuOptions = [
                  { action: 'fold', label: 'Fold' }, 
                  { action: 'call', label: `Call $${currentBet}` }, 
                  { action: 'raise', label: 'Raise' }
                ];
              } else {
                if (street === 'preflop') {
                  if (playerPosition === 'BB') {
                    menuOptions = [
                      { action: 'fold', label: 'Fold' }, 
                      { action: 'check', label: 'Check' }, 
                      { action: 'raise', label: 'Raise' }
                    ];
                  } else {
                    menuOptions = [
                      { action: 'fold', label: 'Fold' }, 
                      { action: 'check', label: 'Check' }, 
                      { action: 'raise', label: 'Raise' }
                    ];
                  }
                } else { 
                  menuOptions = [
                    { action: 'fold', label: 'Fold' }, 
                    { action: 'check', label: 'Check' }, 
                    { action: 'bet', label: 'Bet' }
                  ];
                }
              }
            }

            if (point && menuOptions.length > 0) {
              // Use tighter inner radius for all menus to hug the seats closely
              const menuRadius = radialMenuRadius; // Keep original outer radius
              const menuInnerRadius = radialMenuInnerRadius * 0.5; // Much smaller inner radius to hug seat
              
              const transformString = `translate(${point.x}, ${point.y})`; // Position at seat center
              return (
                <g transform={transformString}>
                  <RadialMenu 
                    options={menuOptions}
                    radius={menuRadius}
                    innerRadius={menuInnerRadius}
                    onSelect={(action) => {
                      handleRadialMenuSelect(action);
                      // Menu will be closed by parent after action is processed
                    }}
                    centerX={0} // Centered within its group
                    centerY={0}
                  />
                </g>
              );
            }
          }
          return null; 
        })()}
                {/* END Radial Menu conditional rendering */}

        {/* Settings Button - Only show in input mode */}
        {isInputMode && (
          <g transform={`translate(${S_WIDTH - 60}, ${isMobile ? 60 : 50})`}>
            {/* Settings button background */}
            <circle 
              cx={0} 
              cy={0} 
              r={isMobile ? 22 : 25} 
              fill="url(#activeTabGradient)" 
              stroke="#d97706" 
              strokeWidth={2}
              filter="url(#tabShadow)"
              style={{ cursor: 'pointer' }}
              onClick={() => setShowSettingsModal(true)}
            />
            
            {/* Settings icon */}
            <g style={{ pointerEvents: 'none' }}>
              {/* Gear icon paths */}
              <circle cx={0} cy={0} r={6} fill="none" stroke="rgba(0,0,0,0.7)" strokeWidth={2} />
              <circle cx={0} cy={0} r={2} fill="rgba(0,0,0,0.7)" />
              {/* Gear teeth */}
              {[0, 60, 120, 180, 240, 300].map((angle, i) => {
                const radian = (angle * Math.PI) / 180;
                const x1 = Math.cos(radian) * 8;
                const y1 = Math.sin(radian) * 8;
                const x2 = Math.cos(radian) * 12;
                const y2 = Math.sin(radian) * 12;
                return (
                  <line 
                    key={i}
                    x1={x1} 
                    y1={y1} 
                    x2={x2} 
                    y2={y2} 
                    stroke="rgba(0,0,0,0.7)" 
                    strokeWidth={2}
                    strokeLinecap="round"
                  />
                );
              })}
            </g>
            
            {/* Tooltip */}
            <text 
              x={0} 
              y={isMobile ? 35 : 40} 
              textAnchor="middle" 
              fill="rgba(255, 255, 255, 0.8)" 
              fontSize={isMobile ? '10' : '12'} 
              fontWeight="600"
              style={{ pointerEvents: 'none' }}
            >
              Settings
            </text>
          </g>
        )}

        {/* Undo Button - Positioned underneath settings button, only show in input mode */}
        {isInputMode && (
          <g transform={`translate(${S_WIDTH - 60}, ${isMobile ? 135 : 125})`}>
            <circle 
              cx={0} 
              cy={0} 
              r={isMobile ? 22 : 25} 
              fill="url(#activeTabGradient)" 
              stroke="#d97706" 
              strokeWidth={2} 
              filter="url(#shadow)"
              onClick={() => {
                if (onUndoLastAction) {
                  onUndoLastAction();
                }
              }}
              style={{ cursor: 'pointer' }}
            />
            
            {/* Undo icon */}
            <text 
              x={0} 
              y={5} 
              textAnchor="middle" 
              dominantBaseline="middle" 
              fill="rgba(0,0,0,0.8)" 
              fontSize={isMobile ? '16' : '18'} 
              style={{ pointerEvents: 'none' }}
            >
              ↶
            </text>
            
            {/* Undo text label */}
            <text 
              x={0} 
              y={isMobile ? 35 : 40} 
              textAnchor="middle" 
              fill="rgba(255, 255, 255, 0.8)" 
              fontSize={isMobile ? '10' : '12'} 
              fontWeight="600"
              style={{ pointerEvents: 'none' }}
            >
              Undo
            </text>
          </g>
        )}

        {/* Analyze Button - Positioned underneath undo button, only show in input mode */}
        {isInputMode && (
          <g transform={`translate(${S_WIDTH - 60}, ${isMobile ? 210 : 200})`}>
            <circle 
              cx={0} 
              cy={0} 
              r={isMobile ? 22 : 25} 
              fill="url(#activeTabGradient)" 
              stroke="#d97706" 
              strokeWidth={2} 
              filter="url(#shadow)"
              onClick={() => {
                if (onAnalyzeHand) {
                  onAnalyzeHand();
                }
              }}
              style={{ cursor: 'pointer' }}
            />
            
            {/* Analyze icon */}
            <text 
              x={0} 
              y={5} 
              textAnchor="middle" 
              dominantBaseline="middle" 
              fill="rgba(0,0,0,0.8)" 
              fontSize={isMobile ? '16' : '18'} 
              style={{ pointerEvents: 'none' }}
            >
              🧠
            </text>
            
            {/* Analyze text label */}
            <text 
              x={0} 
              y={isMobile ? 35 : 40} 
              textAnchor="middle" 
              fill="rgba(255, 255, 255, 0.8)" 
              fontSize={isMobile ? '10' : '12'} 
              fontWeight="600"
              style={{ pointerEvents: 'none' }}
            >
              Analyze
            </text>
          </g>
        )}

        {/* Settings Modal - Only show when open and in input mode */}
        {/* Modal is now rendered as HTML outside the SVG */}

        {/* Street Navigation Tabs - Only show in input mode */}
        {isInputMode && inputModeCurrentStreet && onStreetChange && (
          <g transform={`translate(0, ${S_HEIGHT + 100 - 70})`}>
            {/* Enhanced background with gradient */}
            <defs>
              <linearGradient id="tabBarGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(15, 23, 42, 0.95)" />
                <stop offset="100%" stopColor="rgba(0, 0, 0, 0.95)" />
              </linearGradient>
              <linearGradient id="activeTabGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#fbbf24" />
                <stop offset="100%" stopColor="#f59e0b" />
              </linearGradient>
              <linearGradient id="inactiveTabGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="rgba(55, 65, 81, 0.8)" />
                <stop offset="100%" stopColor="rgba(31, 41, 55, 0.8)" />
              </linearGradient>
              <filter id="tabShadow" x="-50%" y="-50%" width="200%" height="200%">
                <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="rgba(0,0,0,0.3)" />
              </filter>
            </defs>
            
            {/* Enhanced background bar */}
            <rect 
              x={0} 
              y={0} 
              width={S_WIDTH} 
              height={70} 
              fill="url(#tabBarGradient)" 
              stroke="rgba(255, 206, 62, 0.4)" 
              strokeWidth={1}
              filter="url(#tabShadow)"
            />
            
            {/* Decorative top border */}
            <rect 
              x={0} 
              y={0} 
              width={S_WIDTH} 
              height={2} 
              fill="rgba(255, 206, 62, 0.6)" 
            />
            
            {/* Tab buttons */}
            {['preflop', 'flop', 'turn', 'river'].map((street, index) => {
              const isActive = inputModeCurrentStreet === street;
              const tabWidth = S_WIDTH / 4;
              const tabX = index * tabWidth;
              const padding = isMobile ? 4 : 8;
              const tabHeight = isMobile ? 50 : 56;
              const tabY = (70 - tabHeight) / 2;
              
              return (
                <g key={street}>
                  {/* Tab background with enhanced styling */}
                  <rect 
                    x={tabX + padding} 
                    y={tabY} 
                    width={tabWidth - (padding * 2)} 
                    height={tabHeight} 
                    rx={isMobile ? 8 : 10}
                    ry={isMobile ? 8 : 10}
                    fill={isActive ? 'url(#activeTabGradient)' : 'url(#inactiveTabGradient)'} 
                    stroke={isActive ? '#d97706' : 'rgba(156, 163, 175, 0.3)'} 
                    strokeWidth={isActive ? 2 : 1}
                    filter={isActive ? 'url(#tabShadow)' : 'none'}
                    style={{ 
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => onStreetChange(street as Street)}
                  />
                  
                  {/* Active tab inner glow */}
                  {isActive && (
                    <rect 
                      x={tabX + padding + 2} 
                      y={tabY + 2} 
                      width={tabWidth - (padding * 2) - 4} 
                      height={tabHeight - 4} 
                      rx={isMobile ? 6 : 8}
                      ry={isMobile ? 6 : 8}
                      fill="none" 
                      stroke="rgba(255, 255, 255, 0.3)" 
                      strokeWidth={1}
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                  
                  {/* Street indicator dot */}
                  <circle 
                    cx={tabX + tabWidth / 2} 
                    cy={tabY + (isMobile ? 12 : 15)} 
                    r={isMobile ? 3 : 4} 
                    fill={isActive ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 206, 62, 0.7)'} 
                    style={{ pointerEvents: 'none' }}
                  />
                  
                  {/* Tab text with better typography */}
                  <text 
                    x={tabX + tabWidth / 2} 
                    y={tabY + tabHeight / 2 + (isMobile ? 8 : 10)} 
                    textAnchor="middle" 
                    dominantBaseline="middle"
                    fill={isActive ? 'rgba(0, 0, 0, 0.9)' : 'rgba(255, 255, 255, 0.9)'} 
                    fontSize={isMobile ? '14' : '16'} 
                    fontWeight={isActive ? 'bold' : '600'}
                    fontFamily="system-ui, -apple-system, sans-serif"
                    style={{ 
                      cursor: 'pointer', 
                      pointerEvents: 'none',
                      textTransform: 'capitalize',
                      letterSpacing: '0.5px'
                    }}
                  >
                    {street}
                  </text>
                  
                  {/* Enhanced navigation arrows for mobile */}
                  {isMobile && (
                    <>
                      {/* Previous arrow with better styling */}
                      {index > 0 && (
                        <g 
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (index > 0) {
                              const streets = ['preflop', 'flop', 'turn', 'river'];
                              onStreetChange(streets[index - 1] as Street);
                            }
                          }}
                        >
                          <circle 
                            cx={tabX + 16} 
                            cy={tabY + tabHeight / 2} 
                            r={12} 
                            fill="rgba(255, 255, 255, 0.1)" 
                            stroke="rgba(255, 255, 255, 0.3)" 
                            strokeWidth={1}
                          />
                          <text 
                            x={tabX + 16} 
                            y={tabY + tabHeight / 2} 
                            textAnchor="middle" 
                            dominantBaseline="middle"
                            fill={isActive ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)'} 
                            fontSize="12" 
                            fontWeight="bold"
                            style={{ pointerEvents: 'none' }}
                          >
                            ◀
                          </text>
                        </g>
                      )}
                      
                      {/* Next arrow with better styling */}
                      {index < 3 && (
                        <g 
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            if (index < 3) {
                              const streets = ['preflop', 'flop', 'turn', 'river'];
                              onStreetChange(streets[index + 1] as Street);
                            }
                          }}
                        >
                          <circle 
                            cx={tabX + tabWidth - 16} 
                            cy={tabY + tabHeight / 2} 
                            r={12} 
                            fill="rgba(255, 255, 255, 0.1)" 
                            stroke="rgba(255, 255, 255, 0.3)" 
                            strokeWidth={1}
                          />
                          <text 
                            x={tabX + tabWidth - 16} 
                            y={tabY + tabHeight / 2} 
                            textAnchor="middle" 
                            dominantBaseline="middle"
                            fill={isActive ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)'} 
                            fontSize="12" 
                            fontWeight="bold"
                            style={{ pointerEvents: 'none' }}
                          >
                            ▶
                          </text>
                        </g>
                      )}
                    </>
                  )}
                  
                  {/* Tab separator line (except for last tab) */}
                  {index < 3 && (
                    <line 
                      x1={tabX + tabWidth - 1} 
                      y1={tabY + 8} 
                      x2={tabX + tabWidth - 1} 
                      y2={tabY + tabHeight - 8} 
                      stroke="rgba(156, 163, 175, 0.2)" 
                      strokeWidth={1}
                      style={{ pointerEvents: 'none' }}
                    />
                  )}
                </g>
              );
            })}
            
            {/* Bottom highlight bar for active tab */}
            {(() => {
              const activeIndex = ['preflop', 'flop', 'turn', 'river'].indexOf(inputModeCurrentStreet);
              if (activeIndex >= 0) {
                const tabWidth = S_WIDTH / 4;
                const tabX = activeIndex * tabWidth;
                const padding = isMobile ? 4 : 8;
                
                return (
                  <rect 
                    x={tabX + padding + 4} 
                    y={66} 
                    width={tabWidth - (padding * 2) - 8} 
                    height={3} 
                    rx={1.5}
                    fill="#fbbf24" 
                    style={{ pointerEvents: 'none' }}
                  />
                );
              }
              return null;
            })()}
          </g>
        )}
      </g>
    </svg>
    
    {/* HTML Settings Modal - positioned over the SVG with matching styling */}
    {isInputMode && showSettingsModal && (
      <>
        <style>{`
          .settings-modal-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(0, 0, 0, 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 1000;
          }
          
          .settings-modal-content {
            background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
            border: 3px solid rgba(255, 206, 62, 0.6);
            border-radius: 20px;
            box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
            width: 90vw;
            max-width: 600px;
            max-height: 90vh;
            overflow: hidden;
            font-family: system-ui, -apple-system, sans-serif;
          }
          
          .settings-modal-header {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            padding: 1.5rem 2rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 2px solid rgba(255, 206, 62, 0.4);
          }
          
          .settings-modal-title {
            color: rgba(0, 0, 0, 0.9);
            font-size: 1.8rem;
            font-weight: bold;
            margin: 0;
          }
          
          .settings-modal-close {
            background: rgba(0, 0, 0, 0.2);
            border: none;
            border-radius: 50%;
            width: 40px;
            height: 40px;
            color: rgba(0, 0, 0, 0.8);
            font-size: 1.5rem;
            font-weight: bold;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .settings-modal-close:hover {
            background: rgba(0, 0, 0, 0.3);
          }
          
          .settings-modal-body {
            padding: 2rem;
            display: flex;
            flex-direction: column;
            gap: 1.5rem;
          }
          
          .settings-field {
            display: flex;
            justify-content: space-between;
            align-items: center;
            gap: 1rem;
          }
          
          .settings-field label {
            color: rgba(255, 255, 255, 0.9);
            font-size: 1.2rem;
            font-weight: 700;
            flex: 1;
          }
          
          .settings-field-value {
            background: rgba(255, 255, 255, 0.15);
            border: 2px solid rgba(255, 255, 255, 0.4);
            border-radius: 12px;
            padding: 0.8rem 1.2rem;
            color: rgba(255, 255, 255, 0.95);
            font-size: 1.1rem;
            font-weight: 800;
            font-family: monospace;
            text-align: center;
            min-width: 120px;
            flex-shrink: 0;
          }
          
          .settings-input, .settings-select {
            background: rgba(255, 255, 255, 0.15);
            border: 2px solid rgba(255, 255, 255, 0.4);
            border-radius: 8px;
            padding: 0.6rem 0.8rem;
            color: rgba(255, 255, 255, 0.95);
            font-size: 0.95rem;
            font-weight: 600;
            text-align: center;
            min-width: 80px;
            flex-shrink: 0;
            transition: all 0.2s;
          }
          
          .settings-input:focus, .settings-select:focus {
            outline: none;
            border-color: #fbbf24;
            background: rgba(255, 255, 255, 0.2);
          }
          
          .position-buttons {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 3px;
            width: 100%;
            margin-top: 0.2rem;
          }
          
          .position-button {
            background: rgba(255, 255, 255, 0.1);
            border: 1px solid rgba(255, 255, 255, 0.3);
            color: rgba(255, 255, 255, 0.9);
            padding: 0.3rem 0.5rem;
            border-radius: 4px;
            font-size: 0.75rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.2s;
            text-align: center;
            line-height: 1.2;
            height: 24px;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          
          .position-button:hover {
            background: rgba(255, 255, 255, 0.2);
            border-color: #fbbf24;
          }
          
          .position-button.active {
            background: #fbbf24;
            color: rgba(0, 0, 0, 0.9);
            border-color: #fbbf24;
            font-weight: 700;
          }
          
          .settings-modal-actions {
            padding: 1.5rem 2rem;
            display: flex;
            justify-content: space-between;
            gap: 1rem;
            border-top: 1px solid rgba(255, 255, 255, 0.1);
          }
          
          .settings-btn {
            padding: 0.8rem 2rem;
            font-size: 1.1rem;
            font-weight: 700;
            border: none;
            border-radius: 12px;
            cursor: pointer;
            transition: all 0.2s ease;
            min-width: 120px;
          }
          
          .settings-btn-cancel {
            background: rgba(156, 163, 175, 0.8);
            color: rgba(255, 255, 255, 0.9);
            border: 2px solid rgba(156, 163, 175, 0.3);
          }
          
          .settings-btn-cancel:hover {
            background: rgba(156, 163, 175, 1);
          }
          
          .settings-btn-save {
            background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
            color: rgba(0, 0, 0, 0.9);
            border: 2px solid #d97706;
          }
          
          .settings-btn-save:hover {
            background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          }
          
          @media (max-width: 768px) {
            .settings-modal-content {
              width: 75vw;
              margin: 0.25rem;
              max-height: 70vh;
              border-radius: 12px;
            }
            
            .settings-modal-header {
              padding: 0.5rem 0.75rem;
            }
            
            .settings-modal-title {
              font-size: 1rem;
            }
            
            .settings-modal-close {
              width: 30px;
              height: 30px;
              font-size: 1.2rem;
            }
            
            .settings-modal-body {
              padding: 0.75rem;
              gap: 0.6rem;
            }
            
            .settings-field {
              flex-direction: column;
              align-items: stretch;
              gap: 0.3rem;
            }
            
            .settings-field label {
              font-size: 0.85rem;
              font-weight: 600;
            }
            
            .settings-field-value {
              font-size: 0.8rem;
              padding: 0.4rem 0.6rem;
              min-width: auto;
              border-radius: 8px;
            }
            
            .settings-input, .settings-select {
              font-size: 0.8rem;
              padding: 0.5rem 0.6rem;
              min-width: auto;
              border-radius: 6px;
            }
            
            .position-buttons {
              grid-template-columns: repeat(3, 1fr);
              gap: 2px;
            }
            
            .position-button {
              font-size: 0.7rem;
              padding: 0.25rem 0.3rem;
              height: 20px;
              border-radius: 3px;
            }
            
            .settings-modal-actions {
              padding: 0.5rem 0.75rem;
              flex-direction: column;
              gap: 0.4rem;
            }
            
            .settings-btn {
              padding: 0.6rem;
              font-size: 0.8rem;
              min-width: auto;
              border-radius: 8px;
            }
          }
        `}</style>
        
        <div 
          className="settings-modal-overlay"
          onClick={() => setShowSettingsModal(false)}
        >
          <div 
            className="settings-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="settings-modal-header">
              <h3 className="settings-modal-title">Game Settings</h3>
              <button 
                className="settings-modal-close"
                onClick={() => setShowSettingsModal(false)}
              >
                ×
              </button>
            </div>
            
            <div className="settings-modal-body">
              <div className="settings-field">
                <label>Small Blind:</label>
                <input 
                  type="number" 
                  value={editableSettings.smallBlind}
                  onChange={(e) => handleSettingsChange('smallBlind', Number(e.target.value))}
                  className="settings-input"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="settings-field">
                <label>Big Blind:</label>
                <input 
                  type="number" 
                  value={editableSettings.bigBlind}
                  onChange={(e) => handleSettingsChange('bigBlind', Number(e.target.value))}
                  className="settings-input"
                  min="0"
                  step="0.01"
                />
              </div>
              
              <div className="settings-field">
                <label>Players:</label>
                <select 
                  value={editableSettings.seatCount}
                  onChange={(e) => handleSettingsChange('seatCount', Number(e.target.value))}
                  className="settings-select"
                >
                  {[2,3,4,5,6,7,8,9,10].map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>
              
              <div className="settings-field">
                <label>Hero Position:</label>
                <div className="position-buttons">
                  {getAvailablePositions().map((position) => (
                    <button
                      key={position.value}
                      type="button"
                      className={`position-button ${editableSettings.heroPosition === position.value ? 'active' : ''}`}
                      onClick={() => handleSettingsChange('heroPosition', position.value)}
                    >
                      {position.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="settings-modal-actions">
              <button 
                className="settings-btn settings-btn-cancel"
                onClick={handleCancelSettings}
              >
                Cancel
              </button>
              <button 
                className="settings-btn settings-btn-save"
                onClick={handleSaveSettings}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </>
    )}
  </div>
);
};

export default PokerVisual;